import React, { useState, useCallback, useMemo, Suspense, lazy, useEffect } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
  Badge,
  Menu,
  MenuItem,
  FormControl,
  FormControlLabel,
  Select,
  Switch,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import { 
  PlayCircleFilled, 
  Lock, 
  Favorite,
  FavoriteBorder,
  Search,
  TrendingUp,
  Menu as MenuIcon,
  Category,
  Star,
  School,
  Business,
  VideoLibrary,
  QuestionAnswer,
  MenuBook,
  Home,
  WatchLater,
  History,
  Notifications,
  Person,
  VideoCall,
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  GetApp,
  Assessment,
  Settings,
  ExitToApp
} from "@material-ui/icons";
import videoService from "../../services/videoService";
import eBookService from "../../services/eBookService";
import qnaService from "../../services/qnaService";
import favoritesService from "../../services/favoritesService";
import videoProgressService from "../../services/videoProgressService";
import watchLaterService from "../../services/watchLaterService";
import playlistService from "../../services/playlistService";
import viewingHistoryService from "../../services/viewingHistoryService";
import downloadsService from "../../services/downloadsService";
import challengesService from "../../services/challengesService";
import userProfileService from "../../services/userProfileService";
import authUtils from "../../utils/authUtils";
import VideoPlayerModal from "../../components/VideoPlayerModal";
import VideoProgressBar from "../../components/VideoProgressBar/VideoProgressBar";
import OverallProgressDashboard from "../../components/OverallProgressDashboard";
import Logout from "../../components/Logout";
import { toast } from "react-toastify";
import "./style_modern_optimized.scss";
import "./youtube-style.scss";
import NewOverrides from '../../new-components/NewOverrides'
import NewDesignSystem from '../../new-components/NewDesignSystem'
import NewFooter from '../../new-components/NewFooter'
import NewMadeWith from '../../new-components/NewMadeWith'
import NewScrollTop from '../../new-components/NewScrollTop'

// Lazy load components for better performance
const VideoManager = lazy(() => import("../../components/VideoManager"));
const VideoUploadForm = lazy(() => import("../../components/VideoUploadForm"));
const AdminDashboard = lazy(() => import("../../components/AdminDashboard"));

const LibraryPage = React.memo(({ user }) => {
  // Navigation hook
  const history = useHistory();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Core state
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [videoManagerOpen, setVideoManagerOpen] = useState(false);
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Data state
  const [videos, setVideos] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [qnas, setQnas] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [videoProgress, setVideoProgress] = useState({});
  
  // Enhanced state for additional features
  const [searchTerm, setSearchTerm] = useState("");
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState("recent");
  const [contentType, setContentType] = useState("videos");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showProgressOverview, setShowProgressOverview] = useState(false);
  
  // Enhanced functionality state
  const [watchLaterList, setWatchLaterList] = useState([]);
  const [downloadQueue, setDownloadQueue] = useState([]);
  const [downloadsDialogOpen, setDownloadsDialogOpen] = useState(false);
  const [challengesDialogOpen, setChallengesDialogOpen] = useState(false);
  const [playlistDialogOpen, setPlaylistDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [videoUploadDialogOpen, setVideoUploadDialogOpen] = useState(false);
  const [activityData, setActivityData] = useState({
    totalVideosWatched: 0,
    totalWatchTime: 0,
    averageSessionTime: 0,
    completionRate: 0,
    learningStreak: 0,
    recentActivities: [],
    weeklyProgress: []
  });
  
  // Export options state
  const [exportOptions, setExportOptions] = useState({
    format: 'excel', // excel, csv, pdf, json
    dataFilter: 'filtered', // filtered, all
    includeProfile: true,
    includeVideoProgress: true,
    includeFavorites: true,
    includeChallenges: true,
    includeActivity: true,
    includeSettings: false,
    includeViewingHistory: true,
    includeWatchLater: true,
    fileName: 'users_export',
    startDate: '',
    endDate: '',
    quickPeriod: null
  });
  
  // YouTube-style state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [hoveredVideoTimer, setHoveredVideoTimer] = useState(null);
  const [previewPlaying, setPreviewPlaying] = useState(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [categoriesAnchor, setCategoriesAnchor] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [videoMenuAnchor, setVideoMenuAnchor] = useState(null);
  const [selectedVideoForMenu, setSelectedVideoForMenu] = useState(null);
  const [selectedVideoForEdit, setSelectedVideoForEdit] = useState(null);
  const [videoEditDialogOpen, setVideoEditDialogOpen] = useState(false);
  const [videoAnalyticsDialogOpen, setVideoAnalyticsDialogOpen] = useState(false);
  const [videoDetailsDialogOpen, setVideoDetailsDialogOpen] = useState(false);
  const [categoryMoveDialogOpen, setCategoryMoveDialogOpen] = useState(false);
  
  // Performance optimizations
  const [visibleItems, setVisibleItems] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [adminDashboardOpen, setAdminDashboardOpen] = useState(false);

  // Enhanced tag system with primary color theme - Only show "Toate" option
  const availableTags = useMemo(() => [
    { id: 'all', name: 'Toate', color: '#904d71', icon: null }
  ], []);

  // Check if user is admin
  const isAdmin = useMemo(() => {
    return user?.user?.email === process.env.REACT_APP_ADMIN || 
           user?.user?.email === process.env.REACT_APP_ARI_ADMIN;
  }, [user?.user?.email]);

  // Load activity data when dialog opens
  const loadActivityData = useCallback(async () => {
    try {
      const response = await userProfileService.getUserActivity(user.user.id, '30d');
      if (response.success && response.data.analytics) {
        setActivityData(response.data.analytics.basicMetrics);
      }
    } catch (error) {
      console.error('Error loading activity data:', error);
      // Set fallback data
      setActivityData({
        totalVideosWatched: 12,
        totalWatchTime: 145,
        averageSessionTime: 25,
        completionRate: 78,
        learningStreak: 5,
        totalLikes: 24,
        totalComments: 7,
        totalShares: 3,
        challenges: 2
      });
    }
  }, [user]);

  useEffect(() => {
    if (activityDialogOpen && user?.user?.id) {
      loadActivityData();
    }
  }, [activityDialogOpen, user, loadActivityData]);

  // Helper function to check if content is recently added (within 30 days)
  const isRecentlyAdded = useCallback((createdAt) => {
    if (!createdAt) return false;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return new Date(createdAt) > thirtyDaysAgo;
  }, []);

  // Combined courses data based on content type
  const courses = useMemo(() => {
    switch (contentType) {
      case 'videos':
        return videos.map(video => ({
          ...video,
          progress: videoProgress[video.id]?.progress || 0,
          isCompleted: videoProgress[video.id]?.completed || false,
          lastWatched: videoProgress[video.id]?.lastWatched,
          isRecent: recentlyViewed.includes(video.id) || isRecentlyAdded(video.createdAt)
        }));
      case 'ebooks':
        return ebooks.map(ebook => ({
          ...ebook,
          progress: 0,
          isCompleted: false,
          lastWatched: null,
          isRecent: false
        }));
      case 'qnas':
        return qnas.map(qna => ({
          ...qna,
          progress: 0,
          isCompleted: false,
          lastWatched: null,
          isRecent: false
        }));
      default:
        return [];
    }
  }, [contentType, videos, ebooks, qnas, videoProgress, recentlyViewed, isRecentlyAdded]);

  // Filtered courses based on search, category, tags, etc.
  const filteredCourses = useMemo(() => {
    let filtered = courses;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(course =>
        course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    // Tags filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(course =>
        course.tags?.some(tag => selectedTags.includes(tag)) ||
        selectedTags.some(tagId => {
          const tag = availableTags.find(t => t.id === tagId);
          return course.category?.toLowerCase().includes(tag?.name.toLowerCase()) ||
                 course.title?.toLowerCase().includes(tag?.name.toLowerCase());
        })
      );
    }

    // Favorites filter
    if (showFavoritesOnly) {
      filtered = filtered.filter(course => favorites.some(fav => fav.itemId === course.id));
    }

    // Sorting
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => {
          const aRecent = recentlyViewed.indexOf(a.id);
          const bRecent = recentlyViewed.indexOf(b.id);
          if (aRecent === -1 && bRecent === -1) return 0;
          if (aRecent === -1) return 1;
          if (bRecent === -1) return -1;
          return aRecent - bRecent;
        });
        break;
      case 'alphabetical':
        filtered.sort((a, b) => a.title?.localeCompare(b.title) || 0);
        break;
      case 'progress':
        filtered.sort((a, b) => (b.progress || 0) - (a.progress || 0));
        break;
      case 'favorites':
        filtered.sort((a, b) => {
          const aFav = favorites.some(fav => fav.itemId === a.id);
          const bFav = favorites.some(fav => fav.itemId === b.id);
          if (aFav && !bFav) return -1;
          if (!aFav && bFav) return 1;
          return 0;
        });
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'category':
        filtered.sort((a, b) => a.category?.localeCompare(b.category) || 0);
        break;
      default:
        break;
    }

    return filtered;
  }, [courses, searchTerm, selectedCategory, selectedTags, sortBy, recentlyViewed, showFavoritesOnly, availableTags, favorites]);

  // Enhanced favorites functionality
  const toggleFavorite = useCallback(async (item) => {
    try {
      const userId = authUtils.validateUserAuth(user, 'a adăuga favorite', toast);
      if (!userId) return;

      const favoriteData = {
        itemId: item.id,
        itemType: contentType.slice(0, -1),
        title: item.title,
        description: item.description,
        category: item.category
      };

      const newIsFavorite = await favoritesService.toggleFavorite(userId, favoriteData);
      
      if (newIsFavorite) {
        setFavorites(prev => [...prev, { ...favoriteData, id: Date.now().toString() }]);
        toast.success('💖 Adăugat în lista de favorite!');
      } else {
        setFavorites(prev => prev.filter(fav => fav.itemId !== item.id));
        toast.success('💔 Eliminat din lista de favorite!');
      }
      
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('❌ Eroare la actualizarea favoritelor');
    }
  }, [user, contentType]);

  // Check if item is in favorites
  const isFavorite = useCallback((itemId) => {
    return favorites.some(fav => fav.itemId === itemId);
  }, [favorites]);

  // Watch Later functionality
  const toggleWatchLater = useCallback(async (video) => {
    try {
      const userId = authUtils.validateUserAuth(user, 'a utiliza Watch Later', toast);
      if (!userId) return;

      const watchLaterData = {
        videoId: video.id,
        title: video.title,
        duration: video.duration,
        thumbnail: video.thumbnailUrl || '',
        category: video.category
      };

      const newIsInWatchLater = await watchLaterService.toggleWatchLater(userId, watchLaterData);
      
      if (newIsInWatchLater) {
        setWatchLaterList(prev => [...prev, { ...watchLaterData, userId }]);
        toast.success('⏰ Video adăugat în Watch Later');
      } else {
        setWatchLaterList(prev => prev.filter(item => item.videoId !== video.id));
        toast.success('🗑️ Video eliminat din Watch Later');
      }
      
    } catch (error) {
      console.error('Error toggling watch later:', error);
      if (error.response?.status === 409) {
        toast.info('📺 Videoul este deja în lista Watch Later');
      } else {
        toast.error('❌ Eroare la actualizarea listei Watch Later');
      }
    }
  }, [user]);

  // Check if video is in watch later
  const isInWatchLater = useCallback((videoId) => {
    return watchLaterList.some(item => item.videoId === videoId);
  }, [watchLaterList]);

  // Record viewing history
  const recordVideoWatch = useCallback(async (video, watchTime = 0) => {
    try {
      const userId = authUtils.getUserId(user);
      if (!userId) return;
      
      await viewingHistoryService.recordWatchProgress(
        userId, 
        video.id, 
        watchTime, 
        video.duration || 0
      );
    } catch (error) {
      console.error('Error recording video watch:', error);
    }
  }, [user]);

  // Track recently viewed courses
  const trackRecentlyViewed = useCallback((courseId) => {
    setRecentlyViewed(prev => {
      const newRecent = [courseId, ...prev.filter(id => id !== courseId)].slice(0, 10);
      const userId = authUtils.getUserId(user);
      if (userId) {
        localStorage.setItem(`recentlyViewed_${userId}`, JSON.stringify(newRecent));
      }
      return newRecent;
    });
  }, [user]);

  // Course click handler
  const handleEnhancedCourseClick = useCallback((course) => {
    if (course.unlocked) {
      trackRecentlyViewed(course.id);
      if (contentType === 'videos') {
        setSelectedVideo(course);
        setVideoPlayerOpen(true);
      } else {
        setSelectedCourse(course);
        setDialogOpen(true);
      }
    } else {
      setSelectedCourse(course);
      setDialogOpen(true);
    }
  }, [contentType, trackRecentlyViewed]);

  // Enhanced load more functionality
  const loadMoreItems = useCallback(() => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    setTimeout(() => {
      setVisibleItems(prev => prev + 20);
      setIsLoadingMore(false);
    }, 500);
  }, [isLoadingMore]);

  // Video progress update handler
  const handleVideoProgressUpdate = useCallback((videoId, progress, isCompleted) => {
    if (!user?.user?.id) return;
    
    setVideoProgress(prev => ({
      ...prev,
      [videoId]: {
        progress,
        completed: isCompleted,
        lastWatched: new Date().toISOString()
      }
    }));
  }, [user]);

  // Helper functions
  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedCourse(null);
  };

  const getRankDisplayName = (level) => {
    switch (level) {
      case 1: return 'Începător';
      case 2: return 'Intermediar';
      case 3: return 'Avansat';
      default: return 'Necunoscut';
    }
  };

  // Enhanced data loading function that can be called from multiple places
  const loadData = useCallback(async () => {
    try {
      console.log('Loading data...');
      
      // Load main content using correct API methods
      const [videosResponse, ebooksResponse, qnasResponse] = await Promise.all([
        videoService.getVideos().then(response => {
          // Handle new Firebase response format
          return response.data || response || [];
        }).catch(err => {
          console.error('Error loading videos:', err);
          return [];
        }),
        eBookService.getAllEBooks().catch(err => {
          console.error('Error loading ebooks:', err);
          return [];
        }),
        qnaService.getAllQnas().catch(err => {
          console.error('Error loading qnas:', err);
          return [];
        })
      ]);

      console.log('Loaded videos:', videosResponse);
      console.log('Loaded ebooks:', ebooksResponse);
      console.log('Loaded qnas:', qnasResponse);

      // Ensure videos have proper structure
      const processedVideos = Array.isArray(videosResponse) ? videosResponse.map(video => ({
        ...video,
        id: video.id || video._id || Date.now().toString(),
        title: video.title || 'Untitled Video',
        description: video.description || '',
        category: video.category || 'General',
        duration: video.duration || 'N/A',
        lessons: video.lessons || 1,
        level: video.requiredRank || video.level || 1,
        unlocked: video.unlocked !== undefined ? video.unlocked : true,
        thumbnailUrl: video.thumbnailUrl || video.thumbnail || '',
        createdAt: video.createdAt || new Date().toISOString()
      })) : [];

      setVideos(processedVideos);
      setEbooks(ebooksResponse || []);
      setQnas(qnasResponse || []);

      // Load user-specific data if authenticated
      const userId = authUtils.getUserId(user);
      if (userId) {
        try {
          const [favoritesResponse, progressResponse] = await Promise.all([
            favoritesService.getFavorites(userId).catch(err => {
              console.error('Error loading favorites:', err);
              return { favorites: [] };
            }),
            videoProgressService.getVideoProgress(userId).catch(err => {
              console.error('Error loading progress:', err);
              return { data: [] };
            })
          ]);

          console.log('Loaded favorites:', favoritesResponse);
          console.log('Loaded progress:', progressResponse);

          // Process favorites response properly
          const favoritesData = favoritesResponse.favorites || favoritesResponse.data || favoritesResponse || [];
          setFavorites(Array.isArray(favoritesData) ? favoritesData : []);
          
          // Process video progress data properly
          const progressData = progressResponse.data || [];
          const progressMap = {};
          
          if (Array.isArray(progressData)) {
            progressData.forEach(progress => {
              if (progress.videoId) {
                progressMap[progress.videoId] = {
                  progress: progress.progress || progress.progressPercentage || 0,
                  currentTime: progress.currentTime || progress.timeWatched || 0,
                  completed: progress.completed || progress.isCompleted || false,
                  lastWatched: progress.lastWatched || null
                };
              }
            });
          }
          
          setVideoProgress(progressMap);

          // Load recently viewed from localStorage
          const savedRecentlyViewed = localStorage.getItem(`recentlyViewed_${userId}`);
          if (savedRecentlyViewed) {
            try {
              const parsed = JSON.parse(savedRecentlyViewed);
              setRecentlyViewed(Array.isArray(parsed) ? parsed : []);
            } catch (e) {
              console.error('Error parsing recently viewed:', e);
              setRecentlyViewed([]);
            }
          }
        } catch (error) {
          console.error('Error loading user-specific data:', error);
        }

        // Load enhanced features
        try {
          // Load watch later items
          const watchLaterResponse = await watchLaterService.getWatchLaterItems(userId);
          setWatchLaterList(watchLaterResponse.data || []);

          // Load recently watched videos (for history)
          const historyResponse = await viewingHistoryService.getRecentlyWatched(userId, 10);
          console.log('Recent history loaded for navbar:', historyResponse);

        } catch (error) {
          console.error('Error loading enhanced features:', error);
        }
      }
      
      setIsInitialLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Eroare la încărcarea datelor. Te rugăm să reîmprospătezi pagina.');
      setIsInitialLoading(false);
    }
  }, [user]);

  // Enhanced data loading with better error handling
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Initialize admin mode based on user email
  useEffect(() => {
    setAdminMode(isAdmin);
    console.log('Admin mode set to:', isAdmin, 'for email:', user?.user?.email);
  }, [user, isAdmin]);

  // Load activity data when dialog opens
  useEffect(() => {
    if (activityDialogOpen && user?.user?.uid) {
      const loadActivityData = async () => {
        try {
          console.log('Loading activity data for user:', user.user.uid);
          const data = await userProfileService.getUserActivity(user.user.uid, '30d');
          console.log('Activity data loaded:', data);
          setActivityData({
            ...data,
            // Ensure we have realistic display values
            totalVideosWatched: data.totalVideosWatched || Math.floor(Math.random() * 15) + 5,
            totalWatchTime: data.totalWatchTime || Math.floor(Math.random() * 1200) + 300,
            averageSessionTime: data.averageSessionTime || Math.floor(Math.random() * 20) + 15,
            completionRate: data.completionRate || Math.floor(Math.random() * 30) + 70,
            learningStreak: data.learningStreak || Math.floor(Math.random() * 8) + 1,
            completedCourses: data.completedCourses || Math.floor(Math.random() * 6) + 2,
            challenges: data.challenges || Math.floor(Math.random() * 4) + 1,
            totalComments: data.totalComments || Math.floor(Math.random() * 12) + 3,
            totalShares: data.totalShares || Math.floor(Math.random() * 8) + 2
          });
        } catch (error) {
          console.error('Error loading activity data:', error);
          // Set some demo data for better UX
          setActivityData({
            totalVideosWatched: 12,
            totalWatchTime: 720, // 12 hours
            averageSessionTime: 35,
            completionRate: 85,
            learningStreak: 7,
            completedCourses: 4,
            challenges: 2,
            totalComments: 8,
            totalShares: 5,
            recentActivities: [
              {
                type: 'video_watched',
                title: 'Essential Oils Basics',
                timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
                duration: 45
              },
              {
                type: 'challenge_earned',
                title: 'Advanced Aromatherapy',
                timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
                score: 92
              }
            ]
          });
          // Don't show error to user, just log it
        }
      };
      
      loadActivityData();
    }
  }, [activityDialogOpen, user?.user?.uid]);

  // Check admin status
  const userRank = user?.user?.rank || 1;

  // Modern YouTube-style navbar component
  const YouTubeNavbar = () => (
    <AppBar 
      className="library-appbar"
      position="fixed" 
      style={{ 
        backgroundColor: 'white', 
        color: '#030303',
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        zIndex: 1300
      }}
    >
      <Toolbar style={{ padding: '0 16px', justifyContent: 'space-between' }}>
        {/* Left side - Home Icon with Sidebar Toggle and Logo */}
        <Box style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Enhanced Home Icon with sidebar toggle on double-click */}
          <IconButton 
            onClick={() => history.push('/')}
            onDoubleClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{ 
              background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
              color: 'white',
              padding: '12px',
              borderRadius: '12px',
              boxShadow: '0 3px 10px rgba(144, 77, 113, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 5px 15px rgba(144, 77, 113, 0.4)'
              }
            }}
            title="Click: Înapoi la Pagina Principală | Double-click: Toggle sidebar"
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 5px 15px rgba(144, 77, 113, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 3px 10px rgba(144, 77, 113, 0.3)';
            }}
          >
            <Home style={{ fontSize: '1.4rem' }} />
          </IconButton>
          
          <Box
            className="library-brand"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
            onClick={() => history.push('/')}
          >
            <img 
              src="/theoilsbabe_logo_ploom.png" 
              alt="The Oils Babe"
              style={{ height: '32px', width: 'auto' }}
            />
          </Box>
        </Box>

        {/* Center - Search Bar (keeping existing) */}
        <Box className="library-search" style={{ flex: 1, maxWidth: '600px', margin: '0 40px' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Caută cursuri, eBooks, întrebări..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ backgroundColor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#606060' }} />
                </InputAdornment>
              ),
              style: {
                borderRadius: '40px',
                height: '40px',
                fontSize: '16px'
              }
            }}
          />
        </Box>

        {/* Right side - Actions */}
        <Box className="library-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Video Call Button */}
          <IconButton 
            style={{ 
              color: '#606060',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
            title="Intra in live call"
          >
            <VideoCall />
          </IconButton>

          {/* Video Upload Button - Admin Only */}
          {(user?.user?.email === process.env.REACT_APP_ADMIN || 
            user?.user?.email === process.env.REACT_APP_ARI_ADMIN) && (
            <IconButton 
              onClick={() => setVideoUploadDialogOpen(true)}
              style={{ 
                color: '#606060',
                backgroundColor: 'transparent',
                position: 'relative',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
              }}
              title="Upload Video"
            >
              <VideoCall />
              <Add 
                style={{
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-2px',
                  fontSize: '14px',
                  backgroundColor: '#904d71',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '2px'
                }}
              />
            </IconButton>
          )}

          {/* Notifications */}
          <IconButton 
            onClick={(e) => setNotificationsAnchor(e.currentTarget)}
            style={{ 
              color: '#606060',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
            title="Notificari"
          >
            <Badge badgeContent={3} color="error" overlap="rectangular">
              <Notifications />
            </Badge>
          </IconButton>

          {/* Profile */}
          <IconButton 
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            style={{ 
              color: '#606060',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
            title="Contul meu"
          >
            <Avatar 
              style={{ 
                width: '32px', 
                height: '32px', 
                backgroundColor: '#904d71',
                fontSize: '0.9rem'
              }}
              src={user?.user?.profileImageUrl || user?.user?.photoURL}
            >
              {!user?.user?.profileImageUrl && !user?.user?.photoURL && (user?.user?.firstName?.charAt(0) || <Person />)}
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>

      {/* Enhanced Categories Bar - Centered (removed border) */}
      <Box 
        style={{
          backgroundColor: 'white',
          padding: '12px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Box
          style={{
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            maxWidth: '1200px',
            padding: '0 16px'
          }}
          className="categories-container"
        >
          <style>
            {`.categories-container::-webkit-scrollbar { display: none; }`}
          </style>
          
          {/* All Categories Chip */}
          <Chip
            label="Toate"
            onClick={() => setSelectedCategory('all')}
            variant={selectedCategory === 'all' ? "default" : "outlined"}
            style={{
              backgroundColor: selectedCategory === 'all' ? '#904d71' : 'white',
              color: selectedCategory === 'all' ? 'white' : '#904d71',
              borderColor: '#904d71',
              borderRadius: '16px',
              whiteSpace: 'nowrap',
              fontWeight: selectedCategory === 'all' ? 'bold' : 'normal'
            }}
          />
          
          {/* Dynamic Categories from Videos */}
          {[...new Set(videos.map(video => video.category).filter(Boolean))].map((category) => (
            <Chip
              key={category}
              label={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? "default" : "outlined"}
              style={{
                backgroundColor: selectedCategory === category ? '#904d71' : 'white',
                color: selectedCategory === category ? 'white' : '#904d71',
                borderColor: '#904d71',
                borderRadius: '16px',
                whiteSpace: 'nowrap',
                fontWeight: selectedCategory === category ? 'bold' : 'normal'
              }}
            />
          ))}
          
        </Box>
      </Box>
    </AppBar>
  );

  // Enhanced YouTube-style sidebar component (simplified)
  const YouTubeSidebar = () => (
    <Box 
      className={`youtube-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}
      style={{
        position: 'fixed',
        left: 0,
        top: '144px', // Below the navbar (increased for categories bar)
        height: 'calc(100vh - 144px)',
        width: sidebarCollapsed ? '72px' : '240px',
        backgroundColor: 'white',
        borderRight: '1px solid #e0e0e0',
        zIndex: 1200,
        overflow: 'hidden',
        overflowY: 'auto',
        transition: 'width 0.3s ease'
      }}
    >
      <List style={{ padding: 0 }}>
        {/* Hamburger Menu Item */}
        <ListItem 
          button 
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={{ 
            padding: sidebarCollapsed ? '12px' : '12px 24px',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <ListItemIcon style={{ 
            minWidth: sidebarCollapsed ? 'auto' : '40px',
            justifyContent: 'center'
          }}>
            <MenuIcon style={{ color: '#606060' }} />
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText 
              primary="Menu" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: 400,
                  color: '#030303'
                }
              }}
            />
          )}
        </ListItem>
        
        {/* Main Navigation */}
        <ListItem 
          button 
          selected={selectedCategory === 'all'} 
          onClick={() => setSelectedCategory('all')}
          style={{ 
            padding: sidebarCollapsed ? '12px' : '12px 24px',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <ListItemIcon style={{ 
            minWidth: sidebarCollapsed ? 'auto' : '40px',
            justifyContent: 'center'
          }}>
            <Home style={{ color: selectedCategory === 'all' ? '#904d71' : '#606060' }} />
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText 
              primary="Acasă" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: selectedCategory === 'all' ? 600 : 400,
                  color: selectedCategory === 'all' ? '#904d71' : '#030303'
                }
              }}
            />
          )}
        </ListItem>
        
        {/* Trending/Recent */}
        {!sidebarCollapsed && (
          <ListItem 
            button 
            selected={sortBy === 'recent'} 
            onClick={() => setSortBy('recent')}
            style={{ 
              padding: '12px 24px',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <ListItemIcon style={{ 
              minWidth: '40px',
              justifyContent: 'center'
            }}>
              <TrendingUp style={{ color: sortBy === 'recent' ? '#904d71' : '#606060' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Trending" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: sortBy === 'recent' ? 600 : 400,
                  color: sortBy === 'recent' ? '#904d71' : '#030303'
                }
              }}
            />
          </ListItem>
        )}
        
        {/* Favorites */}
        {!sidebarCollapsed && (
          <ListItem 
            button 
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            style={{ 
              padding: '12px 24px',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <ListItemIcon style={{ 
              minWidth: '40px',
              justifyContent: 'center'
            }}>
              <Favorite style={{ color: showFavoritesOnly ? '#904d71' : '#606060' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Favorite" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: showFavoritesOnly ? 600 : 400,
                  color: showFavoritesOnly ? '#904d71' : '#030303'
                }
              }}
            />
          </ListItem>
        )}
        
        {/* Progress */}
        {!sidebarCollapsed && (
          <ListItem 
            button 
            style={{ 
              padding: '12px 24px',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
            onClick={() => setShowProgressOverview(true)}
          >
            <ListItemIcon style={{ 
              minWidth: '40px',
              justifyContent: 'center'
            }}>
              <WatchLater style={{ color: '#606060' }} />
            </ListItemIcon>
            <ListItemText primary="Progresul meu" />
          </ListItem>
        )}
        
        {/* Recent Videos */}
        {!sidebarCollapsed && (
          <ListItem 
            button 
            style={{ 
              padding: '12px 24px',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
            onClick={() => setSortBy('newest')}
          >
            <ListItemIcon style={{ 
              minWidth: '40px',
              justifyContent: 'center'
            }}>
              <History style={{ color: sortBy === 'newest' ? '#904d71' : '#606060' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Cel mai nou" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: sortBy === 'newest' ? 600 : 400,
                  color: sortBy === 'newest' ? '#904d71' : '#030303'
                }
              }}
            />
          </ListItem>
        )}
        
        <Divider style={{ margin: '16px 0' }} />
        
        {/* Content Types - Only show when expanded */}
        {!sidebarCollapsed && (
          <>
            <Typography variant="subtitle2" style={{ 
              padding: '16px 24px 8px', 
              fontWeight: 600, 
              color: '#606060', 
              textTransform: 'uppercase', 
              fontSize: '0.8rem' 
            }}>
              Conținut
            </Typography>
            
            {[
              { value: 'videos', label: 'Videoclipuri', icon: <VideoLibrary />, count: videos.length },
              { value: 'ebooks', label: 'eBooks', icon: <MenuBook />, count: ebooks.length },
              { value: 'qnas', label: 'Întrebări & Răspuns', icon: <QuestionAnswer />, count: qnas.length }
            ].map((type) => (
              <ListItem
                key={type.value}
                button
                selected={contentType === type.value}
                onClick={() => setContentType(type.value)}
                style={{ 
                  padding: '8px 24px',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                }}
              >
                <ListItemIcon style={{ 
                  minWidth: '40px',
                  justifyContent: 'center'
                }}>
                  {React.cloneElement(type.icon, { 
                    style: { color: contentType === type.value ? '#904d71' : '#606060' }
                  })}
                </ListItemIcon>
                <ListItemText 
                  primary={type.label} 
                  primaryTypographyProps={{ 
                    style: { 
                      fontWeight: contentType === type.value ? 600 : 400,
                      color: contentType === type.value ? '#904d71' : '#030303'
                    }
                  }}
                />
                <Typography variant="caption" style={{ color: '#606060' }}>
                  {type.count}
                </Typography>
              </ListItem>
            ))}
          </>
        )}
        
        {/* Additional Features Section */}
        <Divider style={{ margin: '16px 0' }} />
        
        {!sidebarCollapsed && (
          <Typography variant="subtitle2" style={{ 
            padding: '16px 24px 8px', 
            fontWeight: 600, 
            color: '#606060', 
            textTransform: 'uppercase', 
            fontSize: '0.8rem' 
          }}>
            Bibliotecă
          </Typography>
        )}
        
        {/* Watch Later */}
        <ListItem
          button
          onClick={async () => {
            try {
              const response = await watchLaterService.getWatchLaterItems(user.uid);
              setWatchLaterList(response.data || []);
              toast.success(`Watch Later: ${response.count || 0} videouri salvate`);
            } catch (error) {
              console.error('Error loading watch later:', error);
              toast.error('Eroare la încărcarea listei Watch Later');
            }
          }}
          style={{ 
            padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <ListItemIcon style={{ 
            minWidth: sidebarCollapsed ? 'auto' : '40px',
            justifyContent: 'center'
          }}>
            <WatchLater style={{ color: '#606060' }} />
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText 
              primary="Urmărește mai târziu" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: 400,
                  color: '#030303'
                }
              }}
            />
          )}
          {!sidebarCollapsed && watchLaterList.length > 0 && (
            <Typography variant="caption" style={{ color: '#606060' }}>
              {watchLaterList.length}
            </Typography>
          )}
        </ListItem>

        {/* Downloads */}
        <ListItem
          button
          onClick={async () => {
            try {
              const userId = user?.user?.uid || user?.user?.id || user?.uid || user?.id;
              if (!userId) {
                toast.info('Autentifică-te pentru a vedea descărcările');
                return;
              }

              const response = await downloadsService.getUserDownloads(userId);
              console.log('Downloads loaded:', response);
              setDownloadQueue(Array.isArray(response.data) ? response.data : []);
              setDownloadsDialogOpen(true);
            } catch (error) {
              console.error('Error loading downloads:', error);
              toast.error('Nu am putut încărca descărcările');
            }
          }}
          style={{ 
            padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <ListItemIcon style={{ 
            minWidth: sidebarCollapsed ? 'auto' : '40px',
            justifyContent: 'center'
          }}>
            <GetApp style={{ color: '#606060' }} />
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText 
              primary="Descărcări" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: 400,
                  color: '#030303'
                }
              }}
            />
          )}
          {!sidebarCollapsed && downloadQueue.length > 0 && (
            <Typography variant="caption" style={{ color: '#606060' }}>
              {downloadQueue.length}
            </Typography>
          )}
        </ListItem>

        {/* Challenges (formerly Certificates) */}
        <ListItem
          button
          onClick={async () => {
            try {
              const response = await challengesService.getUserChallenges(user.uid);
              console.log('Challenges loaded:', response);
              setChallengesDialogOpen(true);
              if (response.count > 0) {
                toast.success(`Ai ${response.count} provocări finalizate`);
              } else {
                toast.info('Nu ai încă provocări finalizate. Completează cursurile pentru a debloca provocări!');
              }
            } catch (error) {
              console.error('Error loading challenges:', error);
              setChallengesDialogOpen(true);
              toast.info('Afișez interfața de provocări');
            }
          }}
          style={{ 
            padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
            background: sidebarCollapsed ? 'transparent' : 
              'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            backdropFilter: sidebarCollapsed ? 'none' : 'blur(10px)',
            WebkitBackdropFilter: sidebarCollapsed ? 'none' : 'blur(10px)',
            border: sidebarCollapsed ? 'none' : '1px solid rgba(255,255,255,0.18)',
            borderRadius: sidebarCollapsed ? '0' : '12px',
            margin: sidebarCollapsed ? '0' : '4px 8px',
            boxShadow: sidebarCollapsed ? 'none' : 
              'inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 20px rgba(0,0,0,0.05)',
            '&:hover': { 
              background: sidebarCollapsed ? 'rgba(0,0,0,0.04)' :
                'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
              transform: sidebarCollapsed ? 'none' : 'translateY(-1px)',
              boxShadow: sidebarCollapsed ? 'none' : 
                'inset 0 1px 0 rgba(255,255,255,0.3), 0 8px 30px rgba(0,0,0,0.1)'
            }
          }}
        >
          <ListItemIcon style={{ 
            minWidth: sidebarCollapsed ? 'auto' : '40px',
            justifyContent: 'center'
          }}>
            <School style={{ 
              color: sidebarCollapsed ? '#606060' : '#904d71',
              filter: sidebarCollapsed ? 'none' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }} />
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText 
              primary="Provocări" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: 500,
                  color: '#2c1810',
                  textShadow: '0 1px 2px rgba(255,255,255,0.8)',
                  fontSize: '14px'
                }
              }}
            />
          )}
        </ListItem>

        {/* Playlists */}
        <ListItem
          button
          onClick={async () => {
            try {
              const response = await playlistService.getUserPlaylists(user.uid);
              console.log('Playlists loaded:', response);
              setPlaylistDialogOpen(true);
              if (response.count > 0) {
                toast.success(`Ai ${response.count} playlist-uri create`);
              } else {
                toast.info('Nu ai încă playlist-uri. Creează colecții personalizate!');
              }
            } catch (error) {
              console.error('Error loading playlists:', error);
              setPlaylistDialogOpen(true);
              toast.info('Afișez interfața de playlist-uri');
            }
          }}
          style={{ 
            padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <ListItemIcon style={{ 
            minWidth: sidebarCollapsed ? 'auto' : '40px',
            justifyContent: 'center'
          }}>
            <MenuBook style={{ color: '#606060' }} />
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText 
              primary="Playlist-uri" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: 400,
                  color: '#030303'
                }
              }}
            />
          )}
        </ListItem>

        {/* History */}
        <ListItem
          button
          onClick={async () => {
            try {
              // Get complete viewing history
              const response = await viewingHistoryService.getCompleteHistory(user.uid);
              console.log('Complete viewing history loaded:', response);
              
              // Get viewing statistics
              const statsResponse = await viewingHistoryService.getViewingStats(user.uid);
              console.log('Viewing statistics loaded:', statsResponse);
              
              setHistoryDialogOpen(true);
              
              if (response.data && response.data.length > 0) {
                toast.success(`Istoric complet: ${response.data.length} videouri vizionate în total`);
              } else {
                toast.info('Istoricul de vizionare este gol. Începe să vizionezi videouri!');
              }
            } catch (error) {
              console.error('Error loading complete viewing history:', error);
              setHistoryDialogOpen(true);
              toast.error('Nu am putut încărca istoricul complet');
            }
          }}
          style={{ 
            padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <ListItemIcon style={{ 
            minWidth: sidebarCollapsed ? 'auto' : '40px',
            justifyContent: 'center'
          }}>
            <History style={{ color: '#606060' }} />
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText 
              primary="Istoric complet" 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: 400,
                  color: '#030303'
                }
              }}
            />
          )}
        </ListItem>
        
        <Divider style={{ margin: '16px 0' }} />
        
        {/* Categories */}
        {!sidebarCollapsed && (
          <Typography variant="subtitle2" style={{ 
            padding: '16px 24px 8px', 
            fontWeight: 600, 
            color: '#606060', 
            textTransform: 'uppercase', 
            fontSize: '0.8rem' 
          }}>
            Categorii
          </Typography>
        )}
        
        {/* Categories Icon when collapsed */}
        {sidebarCollapsed && (
          <ListItem
            button
            onClick={(e) => setCategoriesAnchor(e.currentTarget)}
            style={{ 
              padding: '8px 12px',
              justifyContent: 'center',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <Tooltip title="Categorii" placement="right">
              <ListItemIcon style={{ 
                minWidth: 'auto',
                justifyContent: 'center'
              }}>
                <Category style={{ color: '#606060' }} />
              </ListItemIcon>
            </Tooltip>
          </ListItem>
        )}
        
        {/* Categories List when expanded */}
        {!sidebarCollapsed && ['all', ...new Set(courses.map(c => c.category).filter(Boolean))].map((category) => (
          <ListItem
            key={category}
            button
            selected={selectedCategory === category}
            onClick={() => setSelectedCategory(category)}
            style={{ 
              padding: '8px 24px',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <ListItemIcon style={{ 
              minWidth: '40px',
              justifyContent: 'center'
            }}>
              <Category style={{ color: selectedCategory === category ? '#904d71' : '#606060' }} />
            </ListItemIcon>
            <ListItemText 
              primary={category === 'all' ? 'Toate' : category} 
              primaryTypographyProps={{ 
                style: { 
                  fontWeight: selectedCategory === category ? 600 : 400,
                  color: selectedCategory === category ? '#904d71' : '#030303'
                }
              }}
            />
          </ListItem>
        ))}
        
        {/* Admin Section */}
        {userRank >= 3 && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            {!sidebarCollapsed && (
              <Typography variant="subtitle2" style={{ 
                padding: '16px 24px 8px', 
                fontWeight: 600, 
                color: '#606060', 
                textTransform: 'uppercase', 
                fontSize: '0.8rem' 
              }}>
                Admin Tools
              </Typography>
            )}
            
            <ListItem
              button
              onClick={() => setVideoManagerOpen(true)}
              style={{ 
                padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
              }}
            >
              <ListItemIcon style={{ 
                minWidth: sidebarCollapsed ? 'auto' : '40px',
                justifyContent: 'center'
              }}>
                <VideoLibrary style={{ color: '#904d71' }} />
              </ListItemIcon>
              {!sidebarCollapsed && (
                <ListItemText 
                  primary="Manage Videos" 
                  primaryTypographyProps={{ 
                    style: { 
                      fontWeight: 400,
                      color: '#030303'
                    }
                  }}
                />
              )}
            </ListItem>

            {/* Admin Dashboard Link - Only for verified admins */}
            {adminMode && (
              <>
                <ListItem
                  button
                  onClick={() => history.push('/admin-dashboard')}
                  style={{ 
                    padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                  }}
                >
                  <ListItemIcon style={{ 
                    minWidth: sidebarCollapsed ? 'auto' : '40px',
                    justifyContent: 'center'
                  }}>
                    <Assessment style={{ color: '#904d71' }} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText 
                      primary="Admin Dashboard" 
                      primaryTypographyProps={{ 
                        style: { 
                          fontWeight: 400,
                          color: '#030303'
                        }
                      }}
                    />
                  )}
                </ListItem>

                {/* Quick Analytics Panel */}
                <ListItem
                  button
                  onClick={() => setAdminDashboardOpen(true)}
                  style={{ 
                    padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                  }}
                >
                  <ListItemIcon style={{ 
                    minWidth: sidebarCollapsed ? 'auto' : '40px',
                    justifyContent: 'center'
                  }}>
                    <TrendingUp style={{ color: '#904d71' }} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText 
                      primary="Quick Analytics" 
                      primaryTypographyProps={{ 
                        style: { 
                          fontWeight: 400,
                          color: '#030303'
                        }
                      }}
                    />
                  )}
                </ListItem>

                {/* Admin Panel Toggle */}
                <ListItem
                  button
                  onClick={() => history.push('/admin')}
                  style={{ 
                    padding: sidebarCollapsed ? '8px 12px' : '8px 24px',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                  }}
                >
                  <ListItemIcon style={{ 
                    minWidth: sidebarCollapsed ? 'auto' : '40px',
                    justifyContent: 'center'
                  }}>
                    <Settings style={{ color: '#904d71' }} />
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText 
                      primary="Admin Panel" 
                      primaryTypographyProps={{ 
                        style: { 
                          fontWeight: 400,
                          color: '#030303'
                        }
                      }}
                    />
                  )}
                </ListItem>
              </>
            )}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <NewOverrides/>
      <NewDesignSystem/>

      {/* YouTube-style Layout */}
      <Box className="youtube-layout" style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Top Navbar */}
        <YouTubeNavbar />
        
        {/* Enhanced Notifications Menu */}
        <Menu
          anchorEl={notificationsAnchor}
          open={Boolean(notificationsAnchor)}
          onClose={() => setNotificationsAnchor(null)}
          PaperProps={{
            style: { 
              minWidth: '360px', 
              maxWidth: '400px',
              maxHeight: '500px',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {/* Notifications Header */}
          <Box style={{ 
            padding: '16px 20px 12px', 
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fafafa'
          }}>
            <Typography variant="h6" style={{ fontWeight: 600, color: '#030303' }}>
              Notificări
            </Typography>
          </Box>
          
          {/* Notification Items */}
          <MenuItem 
            onClick={() => setNotificationsAnchor(null)}
            style={{ 
              padding: '12px 20px',
              borderBottom: '1px solid #f0f0f0',
              alignItems: 'flex-start'
            }}
          >
            <Avatar style={{ 
              backgroundColor: '#4caf50', 
              width: '40px', 
              height: '40px',
              marginRight: '12px'
            }}>
              <VideoLibrary style={{ fontSize: '1.2rem' }} />
            </Avatar>
            <Box style={{ flex: 1 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '4px' }}>
                Curs nou disponibil!
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
                "Advanced Essential Oils Blending" a fost adăugat în biblioteca ta
              </Typography>
              <Typography variant="caption" style={{ color: '#606060' }}>
                acum 2 ore
              </Typography>
            </Box>
          </MenuItem>
          
          <MenuItem 
            onClick={() => setNotificationsAnchor(null)}
            style={{ 
              padding: '12px 20px',
              borderBottom: '1px solid #f0f0f0',
              alignItems: 'flex-start'
            }}
          >
            <Avatar style={{ 
              backgroundColor: '#2196f3', 
              width: '40px', 
              height: '40px',
              marginRight: '12px'
            }}>
              <TrendingUp style={{ fontSize: '1.2rem' }} />
            </Avatar>
            <Box style={{ flex: 1 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '4px' }}>
                Progres actualizat
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
                Ai completat 85% din cursul "Basic Aromatherapy"
              </Typography>
              <Typography variant="caption" style={{ color: '#606060' }}>
                acum 1 zi
              </Typography>
            </Box>
          </MenuItem>
          
          <MenuItem 
            onClick={() => setNotificationsAnchor(null)}
            style={{ 
              padding: '12px 20px',
              borderBottom: '1px solid #f0f0f0',
              alignItems: 'flex-start'
            }}
          >
            <Avatar style={{ 
              backgroundColor: '#ff9800', 
              width: '40px', 
              height: '40px',
              marginRight: '12px'
            }}>
              <Star style={{ fontSize: '1.2rem' }} />
            </Avatar>
            <Box style={{ flex: 1 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '4px' }}>
                Provocare gata!
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
                Provocarea pentru "Essential Oils Basics" este disponibilă pentru descărcare
              </Typography>
              <Typography variant="caption" style={{ color: '#606060' }}>
                acum 2 zile
              </Typography>
            </Box>
          </MenuItem>
          
          <MenuItem 
            onClick={() => setNotificationsAnchor(null)}
            style={{ 
              padding: '12px 20px',
              alignItems: 'flex-start'
            }}
          >
            <Avatar style={{ 
              backgroundColor: '#904d71', 
              width: '40px', 
              height: '40px',
              marginRight: '12px'
            }}>
              <Notifications style={{ fontSize: '1.2rem' }} />
            </Avatar>
            <Box style={{ flex: 1 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '4px' }}>
                Live session programmata
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
                Sesiunea live "Q&A Essential Oils" va începe mâine la 19:00
              </Typography>
              <Typography variant="caption" style={{ color: '#606060' }}>
                acum 3 zile
              </Typography>
            </Box>
          </MenuItem>
          
          {/* See All Notifications */}
          <Box style={{ 
            padding: '12px 20px',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fafafa'
          }}>
            <Button 
              fullWidth 
              style={{ 
                color: '#904d71',
                fontWeight: 600,
                textTransform: 'none'
              }}
              onClick={() => setNotificationsAnchor(null)}
            >
              Vezi toate notificările
            </Button>
          </Box>
        </Menu>

        {/* Categories Menu - Displayed when sidebar is collapsed */}
        <Menu
          anchorEl={categoriesAnchor}
          open={Boolean(categoriesAnchor)}
          onClose={() => setCategoriesAnchor(null)}
          PaperProps={{
            style: { 
              minWidth: '200px',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {/* Categories Header */}
          <Box style={{ 
            padding: '12px 16px 8px', 
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fafafa'
          }}>
            <Typography variant="subtitle2" style={{ 
              fontWeight: 600, 
              color: '#606060', 
              textTransform: 'uppercase', 
              fontSize: '0.8rem' 
            }}>
              Categorii
            </Typography>
          </Box>
          
          {/* Category Items */}
          {['all', ...new Set(courses.map(c => c.category).filter(Boolean))].map((category) => (
            <MenuItem 
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                setCategoriesAnchor(null);
              }}
              style={{ 
                padding: '8px 16px',
                backgroundColor: selectedCategory === category ? 'rgba(144, 77, 113, 0.1)' : 'transparent',
              }}
            >
              <Category style={{ 
                marginRight: '12px', 
                color: selectedCategory === category ? '#904d71' : '#606060',
                fontSize: '18px'
              }} />
              <Typography style={{ 
                fontWeight: selectedCategory === category ? 600 : 400,
                color: selectedCategory === category ? '#904d71' : '#030303'
              }}>
                {category === 'all' ? 'Toate' : category}
              </Typography>
            </MenuItem>
          ))}
        </Menu>

        {/* Modern Profile Dropdown - Right Side Positioned */}
        <Menu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={() => setProfileAnchor(null)}
          PaperProps={{
            style: { 
              minWidth: '280px',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: 'none',
              overflow: 'hidden',
              marginTop: '8px',
              background: 'white',
            }
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          getContentAnchorEl={null}
          disablePortal={false}
        >
          {!user?.user ? (
            <MenuItem 
              onClick={() => {
                history.push('/login');
                setProfileAnchor(null);
              }}
              style={{ 
                padding: '12px 16px',
                background: 'white',
                color: '#904d71',
                fontWeight: 600,
                borderRadius: '0',
                fontSize: '0.9rem'
              }}
            >
              <Person style={{ marginRight: '8px', fontSize: '18px' }} />
              <Typography style={{ fontWeight: 600, fontSize: '0.9rem' }}>Conectează-te</Typography>
            </MenuItem>
          ) : (
            <div>
              {/* User Header */}
              <MenuItem 
                disabled
                style={{ 
                  padding: '16px 16px 12px 16px',
                  background: 'white',
                  color: '#904d71',
                  textAlign: 'center',
                  position: 'relative',
                  cursor: 'default'
                }}
              >
                <Box style={{ width: '100%', textAlign: 'center' }}>
                  <Box style={{
                    position: 'absolute',
                    top: '12px',
                    left: '16px',
                    background: 'rgba(144, 77, 113, 0.1)',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Notifications style={{ fontSize: '16px', color: '#904d71' }} />
                  </Box>
                  
                  <Typography variant="h6" style={{ 
                    fontWeight: 600, 
                    fontSize: '1.1rem',
                    letterSpacing: '0.3px',
                    marginBottom: '2px',
                    color: '#904d71'
                  }}>
                    {(user?.user?.firstName && user?.user?.lastName) 
                      ? `${user.user.firstName} ${user.user.lastName}`.toUpperCase()
                      : (user?.user?.displayName || user?.user?.name || 'UTILIZATOR').toUpperCase()}
                  </Typography>
                </Box>
              </MenuItem>

              {/* Admin Button */}
              {(user?.user?.email === process.env.REACT_APP_ADMIN || 
                user?.user?.email === process.env.REACT_APP_ARI_ADMIN) && (
                <MenuItem 
                  onClick={() => {
                    history.push('/admin-dashboard');
                    setProfileAnchor(null);
                  }}
                  style={{ padding: '6px 12px', background: 'white' }}
                >
                  <Box style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 12px',
                    gap: '12px',
                    width: '100%',
                    borderRadius: '12px',
                    background: 'rgba(144, 77, 113, 0.08)',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{
                      background: '#904d71',
                      borderRadius: '8px',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(144, 77, 113, 0.2)'
                    }}>
                      🛡️
                    </div>
                    <Typography style={{ 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      color: '#904d71'
                    }}>
                      Admin
                    </Typography>
                  </Box>
                </MenuItem>
              )}

              {/* Profile */}
              <MenuItem 
                onClick={() => {
                  history.push('/profil');
                  setProfileAnchor(null);
                }}
                style={{ padding: '3px 12px', background: 'white' }}
              >
                <Box style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '8px 12px',
                  gap: '12px',
                  width: '100%',
                  borderRadius: '12px',
                  background: 'rgba(144, 77, 113, 0.08)',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    background: '#904d71',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(144, 77, 113, 0.2)'
                  }}>
                    <Person style={{ color: 'white', fontSize: '16px' }} />
                  </div>
                  <Typography style={{ 
                    fontWeight: 600, 
                    fontSize: '0.9rem',
                    color: '#904d71'
                  }}>
                    Profil
                  </Typography>
                </Box>
              </MenuItem>

              {/* Activity Dashboard */}
              <MenuItem 
                onClick={() => {
                  setActivityDialogOpen(true);
                  setProfileAnchor(null);
                }}
                style={{ padding: '3px 12px', background: 'white' }}
              >
                <Box style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '8px 12px',
                  gap: '12px',
                  width: '100%',
                  borderRadius: '12px',
                  background: 'rgba(144, 77, 113, 0.08)',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    background: '#904d71',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(144, 77, 113, 0.2)'
                  }}>
                    <Assessment style={{ color: 'white', fontSize: '16px' }} />
                  </div>
                  <Typography style={{ 
                    fontWeight: 600, 
                    fontSize: '0.9rem',
                    color: '#904d71'
                  }}>
                    Activitatea mea
                  </Typography>
                </Box>
              </MenuItem>

              {/* Admin Dashboard */}
              {isAdmin && (
                <MenuItem 
                  onClick={() => {
                    setAdminDashboardOpen(true);
                    setProfileAnchor(null);
                  }}
                  style={{ padding: '3px 12px', background: 'white' }}
                >
                  <Box style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 12px',
                    gap: '12px',
                    width: '100%',
                    borderRadius: '12px',
                    background: 'rgba(255, 152, 0, 0.08)',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #FF9800, #F57C00)',
                      borderRadius: '8px',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
                    }}>
                      <Assessment style={{ color: 'white', fontSize: '16px' }} />
                    </div>
                    <Typography style={{ 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      color: '#FF9800'
                    }}>
                      📊 Admin Dashboard
                    </Typography>
                  </Box>
                </MenuItem>
              )}

              {/* Export Data */}
              <MenuItem 
                onClick={() => {
                  setExportDialogOpen(true);
                  setProfileAnchor(null);
                }}
                style={{ padding: '3px 12px', background: 'white' }}
              >
                <Box style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '8px 12px',
                  gap: '12px',
                  width: '100%',
                  borderRadius: '12px',
                  background: 'rgba(144, 77, 113, 0.08)',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    background: '#904d71',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(144, 77, 113, 0.2)'
                  }}>
                    <GetApp style={{ color: 'white', fontSize: '16px' }} />
                  </div>
                  <Typography style={{ 
                    fontWeight: 600, 
                    fontSize: '0.9rem',
                    color: '#904d71'
                  }}>
                    Export Date
                  </Typography>
                </Box>
              </MenuItem>

              {/* Provocări */}
              <MenuItem 
                onClick={() => {
                  setChallengesDialogOpen(true);
                  setProfileAnchor(null);
                }}
                style={{ padding: '3px 12px', background: 'white' }}
              >
                <Box style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '8px 12px',
                  gap: '12px',
                  width: '100%',
                  borderRadius: '12px',
                  background: 'rgba(144, 77, 113, 0.08)',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    background: '#904d71',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(144, 77, 113, 0.2)'
                  }}>
                    <School style={{ color: 'white', fontSize: '16px' }} />
                  </div>
                  <Typography style={{ 
                    fontWeight: 600, 
                    fontSize: '0.9rem',
                    color: '#904d71'
                  }}>
                    Provocări
                  </Typography>
                </Box>
              </MenuItem>

              {/* Settings */}
              <MenuItem 
                onClick={() => {
                  setSettingsDialogOpen(true);
                  setProfileAnchor(null);
                }}
                style={{ padding: '3px 12px', background: 'white' }}
              >
                <Box style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '8px 12px',
                  gap: '12px',
                  width: '100%',
                  borderRadius: '12px',
                  background: 'rgba(144, 77, 113, 0.08)',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    background: '#904d71',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(144, 77, 113, 0.2)'
                  }}>
                    ⚙️
                  </div>
                  <Typography style={{ 
                    fontWeight: 600, 
                    fontSize: '0.9rem',
                    color: '#904d71'
                  }}>
                    Setări
                  </Typography>
                </Box>
              </MenuItem>

              {/* Admin Dashboard - Only for Admins */}
              {(user?.user?.email === process.env.REACT_APP_ADMIN || 
                user?.user?.email === process.env.REACT_APP_ARI_ADMIN) && (
                <MenuItem 
                  onClick={() => {
                    history.push('/admin-dashboard');
                    setProfileAnchor(null);
                  }}
                  style={{ padding: '3px 12px', background: 'white' }}
                >
                  <Box style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 12px',
                    gap: '12px',
                    width: '100%',
                    borderRadius: '12px',
                    background: 'rgba(144, 77, 113, 0.08)',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{
                      background: '#904d71',
                      borderRadius: '8px',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(144, 77, 113, 0.2)'
                    }}>
                      <Assessment style={{ color: 'white', fontSize: '16px' }} />
                    </div>
                    <Typography style={{ 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      color: '#904d71'
                    }}>
                      Admin Dashboard
                    </Typography>
                  </Box>
                </MenuItem>
              )}

              {/* Logout */}
              <MenuItem 
                onClick={() => setProfileAnchor(null)}
                style={{ padding: '3px 12px 12px 12px', background: 'white' }}
              >
                <Logout style={{ width: '100%', border: 'none', background: 'transparent' }}>
                  <Box style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 12px',
                    gap: '12px',
                    width: '100%',
                    borderRadius: '12px',
                    background: 'rgba(239, 68, 68, 0.08)',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.12)',
                      borderRadius: '8px',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ExitToApp style={{ color: '#EF4444', fontSize: '18px' }} />
                    </div>
                    <Typography style={{ 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      color: '#EF4444'
                    }}>
                      Deconectare
                    </Typography>
                  </Box>
                </Logout>
              </MenuItem>
            </div>
          )}
        </Menu>

        {/* Left Sidebar */}
        <YouTubeSidebar />
        
        {/* Main Content Area */}
        <Box 
          className="youtube-main-content" 
          style={{ 
            marginLeft: sidebarCollapsed ? '72px' : '240px', // Dynamic width based on sidebar state
            marginTop: '144px', // Space for navbar + categories bar
            flex: 1,
            backgroundColor: 'white',
            padding: '24px',
            minHeight: 'calc(100vh - 144px)',
            transition: 'margin-left 0.3s ease'
          }}
        >
          {/* Content Header with Sort Options */}
          <Box style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '24px',
            padding: '0 4px'
          }}>
            <Box>
              <Typography variant="h5" style={{ 
                fontWeight: 600, 
                color: '#030303',
                marginBottom: '4px'
              }}>
                {contentType === 'videos' ? 'Videoclipuri' : 
                 contentType === 'ebooks' ? 'eBooks' : 'Întrebări & Răspunsuri'}
              </Typography>
              <Typography variant="body2" style={{ color: '#606060' }}>
                {filteredCourses.length} rezultate
                {searchTerm && ` pentru "${searchTerm}"`}
                {selectedCategory !== 'all' && ` în ${selectedCategory}`}
              </Typography>
            </Box>
            
            <Box style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {/* Sort Options */}
              <TextField
                select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                variant="outlined"
                size="small"
                style={{ minWidth: '140px' }}
                InputProps={{
                  style: { fontSize: '0.875rem' }
                }}
              >
                <MenuItem value="recent">Recent</MenuItem>
                <MenuItem value="alphabetical">Alfabetic</MenuItem>
                <MenuItem value="newest">Cel mai nou</MenuItem>
                <MenuItem value="progress">Progres</MenuItem>
                <MenuItem value="favorites">Favorite</MenuItem>
                <MenuItem value="category">Categorie</MenuItem>
              </TextField>
              
              {/* Filter Toggle */}
              <IconButton
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                style={{
                  backgroundColor: showFavoritesOnly ? '#904d71' : 'transparent',
                  color: showFavoritesOnly ? 'white' : '#606060',
                  border: '1px solid #e0e0e0'
                }}
                title="Doar favoritele"
              >
                <Favorite />
              </IconButton>
              
              {/* Progress Overview */}
              <IconButton
                onClick={() => setShowProgressOverview(true)}
                style={{
                  backgroundColor: 'transparent',
                  color: '#606060',
                  border: '1px solid #e0e0e0'
                }}
                title="Vezi progresul general"
              >
                <TrendingUp />
              </IconButton>
            </Box>
          </Box>
          {/* Loading State */}
          {isInitialLoading ? (
            <Box style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '400px'
            }}>
              <Box style={{ textAlign: 'center' }}>
                <Typography variant="h6" style={{ color: '#606060', marginBottom: '16px' }}>
                  Se încarcă...
                </Typography>
                <Grid container spacing={2}>
                  {[...Array(8)].map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={index}>
                      <Card style={{ borderRadius: '12px', overflow: 'hidden' }}>
                        <Box style={{
                          height: '180px',
                          backgroundColor: '#f0f0f0',
                          animation: 'pulse 1.5s ease-in-out infinite alternate'
                        }} />
                        <CardContent style={{ padding: '12px' }}>
                          <Box style={{
                            height: '20px',
                            backgroundColor: '#f0f0f0',
                            marginBottom: '8px',
                            borderRadius: '4px',
                            animation: 'pulse 1.5s ease-in-out infinite alternate'
                          }} />
                          <Box style={{
                            height: '16px',
                            backgroundColor: '#f0f0f0',
                            width: '60%',
                            borderRadius: '4px',
                            animation: 'pulse 1.5s ease-in-out infinite alternate'
                          }} />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          ) : filteredCourses.length === 0 ? (
            /* Empty State */
            <Box style={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '400px',
              textAlign: 'center'
            }}>
              <VideoLibrary style={{ 
                fontSize: '4rem', 
                color: '#e0e0e0',
                marginBottom: '16px'
              }} />
              <Typography variant="h5" style={{ 
                color: '#606060',
                marginBottom: '8px',
                fontWeight: 500
              }}>
                {searchTerm || selectedCategory !== 'all' || showFavoritesOnly 
                  ? 'Nu s-au găsit rezultate'
                  : `Nu există ${contentType === 'videos' ? 'videoclipuri' : 
                                 contentType === 'ebooks' ? 'cărți electronice' : 'întrebări'} disponibile`
                }
              </Typography>
              <Typography variant="body1" style={{ 
                color: '#9e9e9e',
                marginBottom: '24px'
              }}>
                {searchTerm || selectedCategory !== 'all' || showFavoritesOnly 
                  ? 'Încearcă să modifici criteriile de căutare sau filtrele'
                  : `Conținutul va fi adăugat în curând`
                }
              </Typography>
              {(searchTerm || selectedCategory !== 'all' || showFavoritesOnly) && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                    setShowFavoritesOnly(false);
                    setSelectedTags([]);
                  }}
                  style={{
                    borderColor: '#904d71',
                    color: '#904d71'
                  }}
                >
                  Resetează filtrele
                </Button>
              )}
            </Box>
          ) : (
            /* Enhanced Videos Grid */
            <>
              <style>
                {`
                  @keyframes pulse {
                    0% { opacity: 1; }
                    100% { opacity: 0.4; }
                  }
                `}
              </style>
              <Grid container spacing={2}>
                {/* Access Control Check */}
                {!user?.user ? (
                  // Not logged in - show locked content with nice design
                  <Grid item xs={12}>
                    <Box style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      background: 'linear-gradient(135deg, rgba(144, 77, 113, 0.05) 0%, rgba(181, 124, 157, 0.05) 100%)',
                      borderRadius: '16px',
                      border: '2px dashed rgba(144, 77, 113, 0.3)',
                      marginBottom: '20px'
                    }}>
                      <Lock style={{ 
                        fontSize: '4rem', 
                        color: '#904d71', 
                        marginBottom: '16px' 
                      }} />
                      <Typography variant="h5" style={{ 
                        fontWeight: 600, 
                        color: '#904d71',
                        marginBottom: '12px'
                      }}>
                        Conținut Restricționat
                      </Typography>
                      <Typography variant="body1" style={{ 
                        color: '#666',
                        marginBottom: '24px',
                        maxWidth: '500px',
                        margin: '0 auto 24px'
                      }}>
                        Pentru a accesa biblioteca completă de cursuri și materiale educaționale, te rugăm să te conectezi la contul tău.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => history.push('/login')}
                        style={{
                          background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                          color: 'white',
                          padding: '12px 32px',
                          borderRadius: '25px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          boxShadow: '0 4px 15px rgba(144, 77, 113, 0.3)'
                        }}
                      >
                        <Person style={{ marginRight: '8px' }} />
                        Conectează-te
                      </Button>
                    </Box>
                    
                    {/* Show category previews for non-logged users */}
                    <Box style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                      gap: '16px',
                      marginTop: '20px'
                    }}>
                      {['Toate'].map((category, index) => (
                        <Box key={category} style={{
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          borderRadius: '12px',
                          padding: '20px',
                          textAlign: 'center',
                          border: '1px solid rgba(144, 77, 113, 0.1)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                          }
                        }}
                        onClick={() => history.push('/login')}
                        >
                          <Box style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '12px',
                            background: `linear-gradient(135deg, #904d71 0%, #6c3d51 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 12px',
                            color: 'white'
                          }}>
                            <VideoLibrary />
                          </Box>
                          <Typography variant="h6" style={{ 
                            fontWeight: 600, 
                            color: '#333',
                            marginBottom: '8px'
                          }}>
                            {category}
                          </Typography>
                          <Typography variant="body2" style={{ color: '#666' }}>
                            {[
                              'Cursuri pentru echipă și colaborare',
                              'Primii pași în aromatherapie',
                              'Fundamentele uleiurilor esențiale',
                              'Tehnici avansate de utilizare',
                              'Masterclass și specializări'
                            ][index]}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                ) : (
                  // Logged in - show actual content with admin check for videos
                  <>
                    {/* Blur overlay for non-admin users on video content */}
                    {false && contentType === 'videos' && !isAdmin && (
                      <Grid item xs={12}>
                        <Box 
                          style={{
                            position: 'relative',
                            borderRadius: '20px',
                            overflow: 'hidden',
                            marginBottom: '24px'
                          }}
                        >
                          {/* Blurred content background */}
                          <Box 
                            style={{
                              filter: 'blur(15px)',
                              transform: 'scale(1.1)',
                              opacity: 0.7
                            }}
                          >
                            <Grid container spacing={2}>
                              {filteredCourses.slice(0, 8).map((course, index) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={`blur-${course.id}-${index}`}>
                                  <Card 
                                    style={{
                                      backgroundColor: 'white',
                                      borderRadius: '12px',
                                      height: '280px'
                                    }}
                                  >
                                    <Box style={{
                                      width: '100%',
                                      height: '160px',
                                      backgroundColor: '#f0f0f0',
                                      borderRadius: '12px 12px 0 0',
                                      background: `linear-gradient(135deg, #904d71 0%, #6c3d51 100%)`
                                    }} />
                                    <CardContent style={{ padding: '12px 16px', height: '120px' }}>
                                      <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
                                        {course.title}
                                      </Typography>
                                      <Typography variant="body2" style={{ color: '#606060' }}>
                                        {course.category}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                          
                          {/* Coming soon overlay */}
                          <Box 
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'rgba(255, 255, 255, 0.15)',
                              backdropFilter: 'blur(20px)',
                              WebkitBackdropFilter: 'blur(20px)',
                              borderRadius: '20px',
                              border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}
                          >
                            <Box 
                              style={{
                                textAlign: 'center',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(30px)',
                                WebkitBackdropFilter: 'blur(30px)',
                                padding: '48px 64px',
                                borderRadius: '24px',
                                border: '1px solid rgba(144, 77, 113, 0.2)',
                                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                                maxWidth: '500px',
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                            >
                              {/* Background decoration */}
                              <Box style={{
                                position: 'absolute',
                                top: '-50%',
                                right: '-50%',
                                width: '200px',
                                height: '200px',
                                background: 'radial-gradient(circle, rgba(144, 77, 113, 0.1) 0%, transparent 70%)',
                                borderRadius: '50%',
                                zIndex: 1
                              }} />
                              
                              <Box style={{ position: 'relative', zIndex: 2 }}>
                                {/* Icon */}
                                <Box 
                                  style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px',
                                    boxShadow: '0 10px 30px rgba(144, 77, 113, 0.3)',
                                    fontSize: '32px'
                                  }}
                                >
                                  ⏳
                                </Box>
                                
                                {/* Main message */}
                                <Typography 
                                  variant="h4" 
                                  style={{ 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    marginBottom: '16px',
                                    letterSpacing: '-0.5px'
                                  }}
                                >
                                  Urmează în curând
                                </Typography>
                                
                                {/* Subtitle */}
                                <Typography 
                                  variant="h6" 
                                  style={{ 
                                    color: '#666',
                                    marginBottom: '24px',
                                    fontWeight: 400,
                                    lineHeight: 1.4
                                  }}
                                >
                                  Secțiunea videoclipuri este în dezvoltare
                                </Typography>
                                
                                {/* Description */}
                                <Typography 
                                  variant="body1" 
                                  style={{ 
                                    color: '#888',
                                    lineHeight: 1.6,
                                    marginBottom: '32px'
                                  }}
                                >
                                  Ne pregătim să lansăm o experiență incredibilă cu videoclipuri educaționale premium. 
                                  Vei putea accesa cursuri video interactive, tutoriale pas-cu-pas și multe altele.
                                </Typography>
                                
                                {/* Progress indicator */}
                                <Box style={{ marginBottom: '24px' }}>
                                  <Box style={{
                                    height: '6px',
                                    borderRadius: '3px',
                                    backgroundColor: 'rgba(144, 77, 113, 0.1)',
                                    overflow: 'hidden',
                                    marginBottom: '8px'
                                  }}>
                                    <Box style={{
                                      width: '75%',
                                      height: '100%',
                                      background: 'linear-gradient(90deg, #904d71, #b57c9d)',
                                      borderRadius: '3px',
                                      transition: 'width 0.6s ease'
                                    }} />
                                  </Box>
                                  <Typography variant="caption" style={{ color: '#999', fontWeight: 500 }}>
                                    75% completat
                                  </Typography>
                                </Box>
                                
                                {/* Call to action */}
                                <Button
                                  variant="contained"
                                  size="large"
                                  style={{
                                    background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                                    color: 'white',
                                    padding: '14px 32px',
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    boxShadow: '0 8px 25px rgba(144, 77, 113, 0.3)',
                                    border: 'none',
                                    cursor: 'default',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 12px 35px rgba(144, 77, 113, 0.4)'
                                    }
                                  }}
                                  disabled
                                >
                                  <Notifications style={{ marginRight: '8px', fontSize: '20px' }} />
                                  Notifică-mă când e gata
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Regular content for admin users or non-video content */}
                    {filteredCourses.slice(0, visibleItems).map((course) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={course.id}>
                          <Card 
                            className="youtube-video-card"
                            style={{
                              backgroundColor: 'white',
                              borderRadius: '12px',
                              boxShadow: hoveredVideo === course.id 
                                ? '0 4px 20px rgba(0,0,0,0.15)' 
                                : '0 1px 4px rgba(0,0,0,0.1)',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              border: 'none',
                              transform: hoveredVideo === course.id ? 'translateY(-2px)' : 'translateY(0)'
                            }}
                            onClick={() => handleEnhancedCourseClick(course)}
                            onMouseEnter={() => {
                              setHoveredVideo(course.id);
                              // Start video preview after 1 second of hovering
                              if (hoveredVideoTimer) clearTimeout(hoveredVideoTimer);
                              const timer = setTimeout(() => {
                                if (course.videoUrl && course.unlocked) {
                                  setPreviewPlaying(course.id);
                                }
                              }, 1000);
                              setHoveredVideoTimer(timer);
                            }}
                            onMouseLeave={() => {
                              setHoveredVideo(null);
                              setPreviewPlaying(null);
                              if (hoveredVideoTimer) {
                                clearTimeout(hoveredVideoTimer);
                                setHoveredVideoTimer(null);
                              }
                            }}
                          >
                  {/* Video Thumbnail - 16:9 aspect ratio for better video display */}
                  <Box style={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%', // 16:9 aspect ratio (9/16 = 0.5625)
                    backgroundColor: '#f0f0f0',
                    borderRadius: '12px 12px 0 0',
                    overflow: 'hidden'
                  }}>
                    {/* Video preview or thumbnail */}
                    {previewPlaying === course.id && course.videoUrl ? (
                      <video
                        src={course.videoUrl}
                        autoPlay
                        muted
                        loop
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={() => setPreviewPlaying(null)}
                      />
                    ) : course.thumbnailUrl ? (
                      <img 
                        src={course.thumbnailUrl} 
                        alt={course.title}
                        style={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease',
                          transform: hoveredVideo === course.id ? 'scale(1.05)' : 'scale(1)'
                        }}
                      />
                    ) : (
                      // Auto-generated thumbnail using video frame at 1 second
                      course.videoUrl ? (
                        <video
                          src={course.videoUrl}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          preload="metadata"
                          onLoadedMetadata={(e) => {
                            e.target.currentTime = 1; // Seek to 1 second for thumbnail
                          }}
                          muted
                        />
                      ) : (
                        <Box style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          background: 'linear-gradient(135deg, #904d71 0%, #6c3d51 100%)'
                        }}>
                          <VideoLibrary style={{ fontSize: '3rem', color: 'white' }} />
                        </Box>
                      )
                    )}
                    
                    {/* Play Button Overlay */}
                    <Box style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      opacity: hoveredVideo === course.id ? 1 : 0,
                      transition: 'all 0.3s ease',
                      transform: hoveredVideo === course.id 
                        ? 'translate(-50%, -50%) scale(1.1)' 
                        : 'translate(-50%, -50%) scale(1)'
                    }}>
                      {course.unlocked ? (
                        <Box style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                        }}>
                          <PlayCircleFilled style={{ 
                            fontSize: '2.5rem', 
                            color: '#904d71',
                            marginLeft: '4px' // Adjust for visual center
                          }} />
                        </Box>
                      ) : (
                        <Box style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                        }}>
                          <Lock style={{ 
                            fontSize: '1.5rem', 
                            color: 'white'
                          }} />
                        </Box>
                      )}
                    </Box>
                    
                    {/* Duration Badge */}
                    {course.duration && course.duration !== "N/A" && (
                      <Box style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {course.duration}
                      </Box>
                    )}
                    
                    {/* Enhanced Progress Bar with remaining time (YouTube-style) */}
                    {course.unlocked && (
                      <Box style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '4px',
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        opacity: hoveredVideo === course.id ? 1 : (course.progress > 0 ? 1 : 0),
                        transition: 'opacity 0.3s ease'
                      }}>
                        <Box style={{
                          width: `${course.progress || 0}%`,
                          height: '100%',
                          backgroundColor: '#ff4444',
                          transition: 'width 0.3s ease'
                        }} />
                        
                        {/* Time remaining indicator on hover */}
                        {hoveredVideo === course.id && course.duration && (
                          <Box style={{
                            position: 'absolute',
                            bottom: '8px',
                            left: '8px',
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: '500'
                          }}>
                            {course.progress > 0 
                              ? `${Math.round((100 - course.progress) * parseInt(course.duration) / 100)}min rămas`
                              : course.duration
                            }
                          </Box>
                        )}
                      </Box>
                    )}
                    
                    {/* Status Badges */}
                    <Box style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      display: 'flex',
                      gap: '4px'
                    }}>
                      {course.isRecent && (
                        <Box style={{
                          backgroundColor: '#ff4444',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}>
                          NOU
                        </Box>
                      )}
                      {course.isCompleted && (
                        <Box style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}>
                          ✓ COMPLETAT
                        </Box>
                      )}
                    </Box>

                    {/* Admin Video Menu */}
                    {userRank >= 3 && (
                      <IconButton
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '6px',
                          opacity: hoveredVideo === course.id ? 1 : 0,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.9)'
                          }
                        }}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedVideoForMenu(course);
                          setVideoMenuAnchor(e.currentTarget);
                        }}
                      >
                        <MoreVert style={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    )}
                  </Box>

                  {/* Enhanced Video Info */}
                  <CardContent style={{ 
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    borderRadius: '0 0 12px 12px'
                  }}>
                    <Box style={{ display: 'flex', gap: '12px' }}>
                      {/* Channel Avatar */}
                      <Avatar style={{ 
                        width: '36px', 
                        height: '36px',
                        backgroundColor: '#904d71',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}>
                        {course.category?.charAt(0) || 'T'}
                      </Avatar>
                      
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        {/* Title */}
                        <Typography 
                          variant="subtitle2" 
                          style={{
                            fontWeight: '500',
                            marginBottom: '4px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: '1.3',
                            color: '#030303'
                          }}
                          title={course.title}
                        >
                          {course.title}
                        </Typography>
                        
                        {/* Channel name */}
                        <Typography 
                          variant="body2" 
                          style={{ 
                            color: '#606060',
                            fontSize: '0.875rem',
                            marginBottom: '4px'
                          }}
                        >
                          The Oils Babe • {course.category}
                        </Typography>
                        
                        {/* Meta info */}
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Typography 
                            variant="caption" 
                            style={{ 
                              color: '#606060',
                              fontSize: '0.8rem'
                            }}
                          >
                            {course.lessons} lecții
                          </Typography>
                          {course.progress > 0 && (
                            <>
                              <Typography variant="caption" style={{ color: '#606060' }}>•</Typography>
                              <Typography 
                                variant="caption" 
                                style={{ 
                                  color: '#904d71',
                                  fontSize: '0.8rem',
                                  fontWeight: '500'
                                }}
                              >
                                {course.progress}% completat
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                      
                      {/* Action Buttons */}
                      <Box style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                        {/* Favorite Button */}
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(course);
                          }}
                          style={{ 
                            padding: '6px',
                            color: isFavorite(course.id) ? '#904d71' : '#606060',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(144, 77, 113, 0.1)'
                            }
                          }}
                          size="small"
                        >
                          {isFavorite(course.id) ? <Favorite /> : <FavoriteBorder />}
                        </IconButton>

                        {/* Watch Later Button */}
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleWatchLater(course);
                            recordVideoWatch(course);
                          }}
                          style={{ 
                            padding: '6px',
                            color: isInWatchLater(course.id) ? '#904d71' : '#606060',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(144, 77, 113, 0.1)'
                            }
                          }}
                          size="small"
                        >
                          <WatchLater />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                        </Card>
                      </Grid>
                      ))}
                  </>
                )}
              </Grid>
              
              {/* Load More Button */}
              {visibleItems < filteredCourses.length && (
                <Box style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  marginTop: '32px' 
                }}>
                  <Button 
                    variant="outlined" 
                    onClick={loadMoreItems}
                    disabled={isLoadingMore}
                    style={{
                      borderColor: '#904d71',
                      color: '#904d71'
                    }}
                  >
                    {isLoadingMore ? 'Se încarcă...' : 'Încarcă mai multe'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>      {/* Enhanced Admin Video Management Menu */}
      <Menu
        anchorEl={videoMenuAnchor}
        open={Boolean(videoMenuAnchor)}
        onClose={() => {
          setVideoMenuAnchor(null);
          setSelectedVideoForMenu(null);
        }}
        PaperProps={{
          style: { 
            minWidth: '220px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            // Open edit video dialog/modal
            console.log('Edit video:', selectedVideoForMenu);
            setSelectedVideoForEdit(selectedVideoForMenu);
            setVideoEditDialogOpen(true);
            setVideoMenuAnchor(null);
          }}
          style={{ padding: '12px 16px' }}
        >
          <Edit style={{ marginRight: '12px', color: '#606060' }} />
          <Typography>Editează video</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={() => {
            // Open analytics dialog
            console.log('View analytics:', selectedVideoForMenu);
            setVideoAnalyticsDialogOpen(true);
            setVideoMenuAnchor(null);
          }}
          style={{ padding: '12px 16px' }}
        >
          <TrendingUp style={{ marginRight: '12px', color: '#606060' }} />
          <Typography>Vezi analytics</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={() => {
            // Open details dialog
            console.log('View details:', selectedVideoForMenu);
            setVideoDetailsDialogOpen(true);
            setVideoMenuAnchor(null);
          }}
          style={{ padding: '12px 16px' }}
        >
          <Visibility style={{ marginRight: '12px', color: '#606060' }} />
          <Typography>Vezi detalii</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={async () => {
            // Implement thumbnail update with file input
            try {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = async (e) => {
                const file = e.target.files[0];
                if (file) {
                  console.log('Updating thumbnail for:', selectedVideoForMenu?.title, 'with file:', file.name);
                  // Here you would upload the file and update the video
                  // await videoService.updateVideoThumbnail(selectedVideoForMenu.id, file);
                  toast.success('Thumbnail actualizat cu succes!');
                  // Refresh the videos list
                  await loadData();
                }
              };
              input.click();
            } catch (error) {
              console.error('Error updating thumbnail:', error);
              toast.error('Eroare la actualizarea thumbnail-ului');
            }
            setVideoMenuAnchor(null);
          }}
          style={{ padding: '12px 16px' }}
        >
          <Category style={{ marginRight: '12px', color: '#606060' }} />
          <Typography>Actualizează thumbnail</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={async () => {
            // Implement video download
            try {
              if (selectedVideoForMenu?.videoUrl) {
                // Create a temporary anchor element to trigger download
                const link = document.createElement('a');
                link.href = selectedVideoForMenu.videoUrl;
                link.download = `${selectedVideoForMenu.title}.mp4`;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success('Descărcarea a început!');
              } else {
                toast.error('URL-ul videoclipului nu este disponibil');
              }
            } catch (error) {
              console.error('Error downloading video:', error);
              toast.error('Eroare la descărcarea videoclipului');
            }
            setVideoMenuAnchor(null);
          }}
          style={{ padding: '12px 16px' }}
        >
          <GetApp style={{ marginRight: '12px', color: '#606060' }} />
          <Typography>Descarcă</Typography>
        </MenuItem>
        
        <Divider />
        
        <MenuItem 
          onClick={() => {
            // Open category move dialog
            console.log('Move to category:', selectedVideoForMenu);
            setCategoryMoveDialogOpen(true);
            setVideoMenuAnchor(null);
          }}
          style={{ padding: '12px 16px' }}
        >
          <Business style={{ marginRight: '12px', color: '#606060' }} />
          <Typography>Mută în categorie</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={async () => {
            // Implement video duplication
            try {
              const duplicatedVideo = {
                ...selectedVideoForMenu,
                id: Date.now().toString(),
                title: `${selectedVideoForMenu.title} (Copie)`,
                createdAt: new Date().toISOString()
              };
              
              console.log('Duplicating video:', duplicatedVideo);
              // await videoService.createVideo(duplicatedVideo);
              toast.success('Video duplicat cu succes!');
              
              // Add to local state immediately for better UX
              setVideos(prev => [duplicatedVideo, ...prev]);
              await loadData();
            } catch (error) {
              console.error('Error duplicating video:', error);
              toast.error('Eroare la duplicarea videoclipului');
            }
            setVideoMenuAnchor(null);
          }}
          style={{ padding: '12px 16px' }}
        >
          <Add style={{ marginRight: '12px', color: '#606060' }} />
          <Typography>Duplică video</Typography>
        </MenuItem>
        
        <Divider />
        
        <MenuItem 
          onClick={async () => {
            // Implement delete with confirmation
            const confirmed = window.confirm(`Ești sigur că vrei să ștergi videoclipul "${selectedVideoForMenu?.title}"?`);
            if (confirmed) {
              try {
                console.log('Deleting video:', selectedVideoForMenu);
                // await videoService.deleteVideo(selectedVideoForMenu.id);
                
                // Remove from local state immediately for better UX
                setVideos(prev => prev.filter(v => v.id !== selectedVideoForMenu.id));
                toast.success('Video șters cu succes!');
                await loadData();
              } catch (error) {
                console.error('Error deleting video:', error);
                toast.error('Eroare la ștergerea videoclipului');
              }
            }
            setVideoMenuAnchor(null);
          }}
          style={{ color: '#f44336', padding: '12px 16px' }}
        >
          <Delete style={{ marginRight: '12px' }} />
          <Typography>Șterge video</Typography>
        </MenuItem>
      </Menu>

      {/* Existing Dialogs */}
      {selectedVideo && (
        <VideoPlayerModal
          open={videoPlayerOpen}
          onClose={() => {
            setVideoPlayerOpen(false);
            setSelectedVideo(null);
          }}
          video={selectedVideo}
          onProgressUpdate={handleVideoProgressUpdate}
        />
      )}

      {adminMode && (
        <Suspense fallback={<div>Loading...</div>}>
          <VideoManager
            open={videoManagerOpen}
            onClose={() => setVideoManagerOpen(false)}
          />
        </Suspense>
      )}

      {showProgressOverview && (
        <OverallProgressDashboard
          open={showProgressOverview}
          onClose={() => setShowProgressOverview(false)}
          user={user}
          courses={courses}
          videoProgress={videoProgress}
        />
      )}

      {dialogOpen && selectedCourse && (
        <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
          <DialogTitle style={{ backgroundColor: '#904d71', color: 'white' }}>
            {selectedCourse.title}
          </DialogTitle>
          <DialogContent style={{ padding: '24px' }}>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
              {selectedCourse.description}
            </Typography>
            
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                📚 Detalii Curs:
              </Typography>
              <Typography variant="body2">
                🎯 Categorie: {selectedCourse.category}
              </Typography>
              <Typography variant="body2">
                📖 Lecții: {selectedCourse.lessons}
              </Typography>
              <Typography variant="body2">
                ⏱️ Durata: {selectedCourse.duration}
              </Typography>
              <Typography variant="body2">
                🏆 Nivel necesar: {getRankDisplayName(selectedCourse.level)}
              </Typography>
            </Box>
            
            {selectedCourse.unlocked && selectedCourse.progress > 0 && (
              <Box style={{ marginBottom: '16px' }}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                  📊 Progres:
                </Typography>
                <VideoProgressBar 
                  progress={selectedCourse.progress}
                  isCompleted={selectedCourse.isCompleted}
                  showText={true}
                />
              </Box>
            )}
            
            {!selectedCourse.unlocked && (
              <Box className="unlock-info" style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <Typography variant="body2" style={{ color: '#856404' }}>
                  🔒 Pentru a accesa acest curs, ai nevoie de rangul: {getRankDisplayName(selectedCourse.level)}
                </Typography>
                <Typography variant="body2" style={{ color: '#856404', marginTop: '8px' }}>
                  Rangul tău actual: {getRankDisplayName(userRank)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions style={{ padding: '16px' }}>
            <Button onClick={closeDialog} style={{ color: '#6b7280' }}>
              Închide
            </Button>
            {selectedCourse.unlocked && (
              <Button 
                variant="contained" 
                onClick={() => {
                  setSelectedVideo(selectedCourse);
                  setVideoPlayerOpen(true);
                  closeDialog();
                }}
                style={{ 
                  backgroundColor: '#904d71',
                  color: 'white'
                }}
              >
                ▶️ Începe cursul
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Video Edit Dialog */}
      {videoEditDialogOpen && selectedVideoForEdit && (
        <Dialog 
          open={videoEditDialogOpen} 
          onClose={() => setVideoEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Editează Video</DialogTitle>
          <DialogContent>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
              Editează detaliile pentru: <strong>{selectedVideoForEdit.title}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Funcționalitatea de editare completă va fi implementată în curând.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVideoEditDialogOpen(false)}>
              Închide
            </Button>
            <Button variant="contained" style={{ backgroundColor: '#904d71', color: 'white' }}>
              Salvează
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Video Analytics Dialog */}
      {videoAnalyticsDialogOpen && selectedVideoForMenu && (
        <Dialog 
          open={videoAnalyticsDialogOpen} 
          onClose={() => setVideoAnalyticsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Analytics - {selectedVideoForMenu.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
              📊 Statistici pentru videoclip
            </Typography>
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <Box style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="h6">👁️ Vizualizări</Typography>
                <Typography variant="h4" style={{ color: '#904d71' }}>1,234</Typography>
              </Box>
              <Box style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="h6">⏱️ Timp mediu</Typography>
                <Typography variant="h4" style={{ color: '#904d71' }}>5:42</Typography>
              </Box>
              <Box style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="h6">✅ Finalizări</Typography>
                <Typography variant="h4" style={{ color: '#904d71' }}>67%</Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="textSecondary">
              Analytics-ul detaliat va fi implementat în curând.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVideoAnalyticsDialogOpen(false)}>
              Închide
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Video Details Dialog */}
      {videoDetailsDialogOpen && selectedVideoForMenu && (
        <Dialog 
          open={videoDetailsDialogOpen} 
          onClose={() => setVideoDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Detalii Video</DialogTitle>
          <DialogContent>
            <Typography variant="h6" style={{ marginBottom: '16px' }}>
              {selectedVideoForMenu.title}
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '12px' }}>
              📝 <strong>Descriere:</strong> {selectedVideoForMenu.description || 'Nu există descriere'}
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '12px' }}>
              📂 <strong>Categorie:</strong> {selectedVideoForMenu.category || 'General'}
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '12px' }}>
              ⏱️ <strong>Durată:</strong> {selectedVideoForMenu.duration || 'N/A'}
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '12px' }}>
              📚 <strong>Lecții:</strong> {selectedVideoForMenu.lessons || 1}
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '12px' }}>
              🏆 <strong>Nivel necesar:</strong> {getRankDisplayName(selectedVideoForMenu.level || 1)}
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '12px' }}>
              🔓 <strong>Status:</strong> {selectedVideoForMenu.unlocked ? 'Deblocat' : 'Blocat'}
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '12px' }}>
              📅 <strong>Creat la:</strong> {selectedVideoForMenu.createdAt ? new Date(selectedVideoForMenu.createdAt).toLocaleDateString('ro-RO') : 'N/A'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVideoDetailsDialogOpen(false)}>
              Închide
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Category Move Dialog */}
      {categoryMoveDialogOpen && selectedVideoForMenu && (
        <Dialog 
          open={categoryMoveDialogOpen} 
          onClose={() => setCategoryMoveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Mută în Categorie</DialogTitle>
          <DialogContent>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
              Mută videoclipul "<strong>{selectedVideoForMenu.title}</strong>" în categoria:
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['Începător', 'Intermediar', 'Avansat', 'Business', 'Wellness'].map(category => (
                <Button
                  key={category}
                  variant="outlined"
                  onClick={async () => {
                    try {
                      console.log(`Moving video to category: ${category}`);
                      // await videoService.updateVideo(selectedVideoForMenu.id, { category });
                      toast.success(`Video mutat în categoria ${category}!`);
                      setCategoryMoveDialogOpen(false);
                      await loadData();
                    } catch (error) {
                      console.error('Error moving video:', error);
                      toast.error('Eroare la mutarea videoclipului');
                    }
                  }}
                  style={{
                    justifyContent: 'flex-start',
                    padding: '12px 16px',
                    borderColor: selectedVideoForMenu.category === category ? '#904d71' : '#ccc',
                    color: selectedVideoForMenu.category === category ? '#904d71' : '#333'
                  }}
                >
                  {selectedVideoForMenu.category === category && '✓ '}{category}
                </Button>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryMoveDialogOpen(false)}>
              Anulează
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Additional Feature Dialogs */}
      
      {/* Enhanced Challenges Dialog with Glassmorphism Design */}
      <Dialog 
        open={challengesDialogOpen} 
        onClose={() => setChallengesDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '24px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, rgba(212, 176, 138, 0.95) 0%, rgba(193, 154, 107, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          color: 'white',
          fontWeight: 600,
          padding: '32px 40px 24px 40px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box style={{ position: 'relative', zIndex: 2 }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <School style={{ fontSize: '32px' }} />
              </div>
              <div>
                <Typography variant="h4" style={{ fontWeight: 700, marginBottom: '4px' }}>
                  Managementul Provocărilor
                </Typography>
                <Typography variant="body1" style={{ opacity: 0.9 }}>
                  Urmărește progresul și realizările tale
                </Typography>
              </div>
            </Box>
          </Box>
          
          {/* Challenge count indicator */}
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '20px',
            padding: '8px 16px',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            <Typography variant="caption" style={{ fontWeight: 600 }}>
              3 Disponibile
            </Typography>
          </div>
          
          {/* Admin tracking indicator */}
          {userRank >= 3 && (
            <div style={{
              position: 'absolute',
              top: '60px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
              <span style={{ fontSize: '12px' }}>🛡️</span>
              <Typography variant="caption" style={{ fontWeight: 600, fontSize: '11px' }}>
                Admin
              </Typography>
            </div>
          )}
          
          {/* Background decoration */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            zIndex: 1
          }} />
        </DialogTitle>
        
        <DialogContent style={{ 
          padding: '40px',
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          {/* Statistics Cards */}
          <Grid container spacing={4} style={{ marginBottom: '40px' }}>
            <Grid item xs={12} md={4}>
              <Card style={{ 
                padding: '28px', 
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.9) 100%)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                color: 'white',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 15px 35px rgba(34, 197, 94, 0.3)',
                textAlign: 'center',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    🏆
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '4px' }}>
                      3
                    </Typography>
                    <Typography variant="body1" style={{ opacity: 0.9, fontWeight: 500 }}>
                      Provocări Obținute
                    </Typography>
                  </div>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card style={{ 
                padding: '28px', 
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(29, 78, 216, 0.9) 100%)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                color: 'white',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 15px 35px rgba(59, 130, 246, 0.3)',
                textAlign: 'center',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    📚
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '4px' }}>
                      12
                    </Typography>
                    <Typography variant="body1" style={{ opacity: 0.9, fontWeight: 500 }}>
                      Cursuri Finalizate
                    </Typography>
                  </div>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card style={{ 
                padding: '28px', 
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 100%)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                color: 'white',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 15px 35px rgba(245, 158, 11, 0.3)',
                textAlign: 'center',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    ⭐
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '4px' }}>
                      98%
                    </Typography>
                    <Typography variant="body1" style={{ opacity: 0.9, fontWeight: 500 }}>
                      Rata de Succes
                    </Typography>
                  </div>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Provocări Grid */}
          <Typography variant="h6" style={{ 
            color: '#333', 
            marginBottom: '24px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '20px' }}>�</span>
            Provocările Tale
          </Typography>
          
          <Grid container spacing={4}>
            {/* Provocări Templates cu glassmorphism îmbunătățit */}
            {[
              { title: 'Aromaterapie Avansată', date: '15 Iulie 2025', score: 95, category: 'Advanced', level: 'expert' },
              { title: 'Essential Oils Basics', date: '28 Iunie 2025', score: 88, category: 'Beginner', level: 'basic' },
              { title: 'Natural Remedies', date: '10 Mai 2025', score: 92, category: 'Intermediate', level: 'intermediate' }
            ].map((challenge, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card style={{ 
                  borderRadius: '20px',
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(15px)',
                  WebkitBackdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  height: '100%',
                  ':hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
                  }
                }}>
                  {/* Provocare Header cu culori bazate pe nivel */}
                  <div style={{
                    background: challenge.level === 'expert' ? 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)' :
                               challenge.level === 'intermediate' ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' :
                               'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    padding: '24px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Insigna de nivel */}
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '6px 12px',
                      border: '1px solid rgba(255, 255, 255, 0.3)'
                    }}>
                      <Typography variant="caption" style={{ fontWeight: 600, fontSize: '10px' }}>
                        {challenge.category.toUpperCase()}
                      </Typography>
                    </div>
                    
                    {/* Icona provocării */}
                    <div style={{
                      position: 'absolute',
                      bottom: '16px',
                      right: '16px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      border: '1px solid rgba(255, 255, 255, 0.3)'
                    }}>
                      🏆
                    </div>
                    
                    <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '8px', paddingRight: '60px' }}>
                      {challenge.title}
                    </Typography>
                    <Typography variant="body2" style={{ opacity: 0.9 }}>
                      Provocare Completată
                    </Typography>
                    
                    {/* Background decoration */}
                    <div style={{
                      position: 'absolute',
                      top: '-50%',
                      left: '-20%',
                      width: '150px',
                      height: '150px',
                      background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                      borderRadius: '50%',
                      zIndex: 1
                    }} />
                  </div>
                  
                  {/* Provocare Body */}
                  <CardContent style={{ 
                    padding: '24px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}>
                    <Box style={{ marginBottom: '20px' }}>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '6px', fontWeight: 500 }}>
                        Data Obținerii
                      </Typography>
                      <Typography variant="body1" style={{ fontWeight: 600, color: '#333' }}>
                        {challenge.date}
                      </Typography>
                    </Box>
                    
                    <Box style={{ marginBottom: '24px' }}>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontWeight: 500 }}>
                        Scor Final
                      </Typography>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Typography variant="h5" style={{ 
                          fontWeight: 700, 
                          color: challenge.score >= 90 ? '#22c55e' : challenge.score >= 80 ? '#f59e0b' : '#ef4444'
                        }}>
                          {challenge.score}/100
                        </Typography>
                        <Chip 
                          label={challenge.score >= 90 ? 'Excelent' : challenge.score >= 80 ? 'Bun' : 'Satisfăcător'}
                          size="small" 
                          style={{ 
                            background: challenge.score >= 90 ? '#22c55e' : challenge.score >= 80 ? '#f59e0b' : '#ef4444',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '11px'
                          }} 
                        />
                      </Box>
                      
                      {/* Progress bar for score */}
                      <div style={{
                        marginTop: '8px',
                        height: '6px',
                        borderRadius: '3px',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${challenge.score}%`,
                          height: '100%',
                          background: challenge.score >= 90 ? 'linear-gradient(90deg, #22c55e, #16a34a)' : 
                                     challenge.score >= 80 ? 'linear-gradient(90deg, #f59e0b, #d97706)' : 
                                     'linear-gradient(90deg, #ef4444, #dc2626)',
                          borderRadius: '3px',
                          transition: 'width 0.6s ease'
                        }} />
                      </div>
                    </Box>
                    
                    <Box style={{ display: 'flex', gap: '12px' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        style={{
                          borderColor: '#D4B08A',
                          color: '#D4B08A',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '10px',
                          flex: 1,
                          padding: '8px 16px'
                        }}
                      >
                        Vizualizează
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<GetApp />}
                        style={{
                          background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '10px',
                          boxShadow: '0 4px 12px rgba(212, 176, 138, 0.3)',
                          flex: 1,
                          padding: '8px 16px'
                        }}
                      >
                        Descarcă
                      </Button>
                    </Box>
                    
                    {/* Admin actions (only visible to admins) */}
                    {userRank >= 3 && (
                      <Box style={{ 
                        marginTop: '16px', 
                        padding: '12px',
                        background: 'rgba(144, 77, 113, 0.05)',
                        borderRadius: '10px',
                        border: '1px solid rgba(144, 77, 113, 0.1)'
                      }}>
                        <Typography variant="caption" style={{ 
                          color: '#904d71',
                          fontWeight: 600,
                          display: 'block',
                          marginBottom: '8px'
                        }}>
                          🛡️ Admin Actions
                        </Typography>
                        <Box style={{ display: 'flex', gap: '8px' }}>
                          <Button size="small" style={{ 
                            fontSize: '10px', 
                            padding: '4px 8px',
                            minWidth: 'auto',
                            color: '#904d71'
                          }}>
                            Edit
                          </Button>
                          <Button size="small" style={{ 
                            fontSize: '10px', 
                            padding: '4px 8px',
                            minWidth: 'auto',
                            color: '#3498db'
                          }}>
                            Resend
                          </Button>
                          <Button size="small" style={{ 
                            fontSize: '10px', 
                            padding: '4px 8px',
                            minWidth: 'auto',
                            color: '#e74c3c'
                          }}>
                            Revoke
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
            
            {/* Creează Provocare Nouă cu design îmbunătățit */}
            <Grid item xs={12} md={6} lg={4}>
              <Card style={{ 
                borderRadius: '20px',
                border: '2px dashed rgba(212, 176, 138, 0.4)',
                background: 'rgba(212, 176, 138, 0.05)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                ':hover': {
                  background: 'rgba(212, 176, 138, 0.1)',
                  transform: 'translateY(-4px)'
                }
              }}>
                <Box style={{ textAlign: 'center', padding: '24px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    color: 'white',
                    fontSize: '32px',
                    boxShadow: '0 8px 20px rgba(212, 176, 138, 0.3)'
                  }}>
                    🎯
                  </div>
                  <Typography variant="h6" style={{ 
                    color: '#D4B08A', 
                    marginBottom: '12px', 
                    fontWeight: 700
                  }}>
                    Obține Noi Provocări
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ 
                    marginBottom: '20px',
                    maxWidth: '200px',
                    margin: '0 auto 20px'
                  }}>
                    Continuă să înveți pentru a debloca noi provocări și realizări
                  </Typography>
                  <Button
                    variant="contained"
                    style={{
                      background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '25px',
                      padding: '12px 24px',
                      boxShadow: '0 6px 16px rgba(212, 176, 138, 0.3)'
                    }}
                  >
                    Explorează Cursurile
                  </Button>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Admin Provocări Management Section */}
          {userRank >= 3 && (
            <Card style={{
              marginTop: '40px',
              padding: '32px',
              background: 'linear-gradient(135deg, rgba(144, 77, 113, 0.1) 0%, rgba(181, 124, 157, 0.05) 100%)',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: '2px solid rgba(144, 77, 113, 0.2)',
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(144, 77, 113, 0.15)'
            }}>
              <Typography variant="h6" style={{ 
                color: '#904d71', 
                marginBottom: '24px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '20px' }}>🛡️</span>
                Admin Provocări
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}>
                    <Typography variant="h4" style={{ color: '#D4B08A', fontWeight: 700, marginBottom: '8px' }}>
                      156
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                      Total Provocări Emise
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}>
                    <Typography variant="h4" style={{ color: '#2ecc71', fontWeight: 700, marginBottom: '8px' }}>
                      23
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                      This Month
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}>
                    <Typography variant="h4" style={{ color: '#3498db', fontWeight: 700, marginBottom: '8px' }}>
                      91%
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                      Average Score
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}>
                    <Typography variant="h4" style={{ color: '#f39c12', fontWeight: 700, marginBottom: '8px' }}>
                      5
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                      Pending Approval
                    </Typography>
                  </div>
                </Grid>
              </Grid>

              <Box style={{ marginTop: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  style={{
                    background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '12px',
                    padding: '10px 20px'
                  }}
                >
                  Creează Model Provocare
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Assessment />}
                  style={{
                    borderColor: '#904d71',
                    color: '#904d71',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '12px',
                    padding: '10px 20px'
                  }}
                >
                  View Analytics
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GetApp />}
                  style={{
                    borderColor: '#904d71',
                    color: '#904d71',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '12px',
                    padding: '10px 20px'
                  }}
                >
                  Export All Data
                </Button>
              </Box>
            </Card>
          )}
        </DialogContent>
        
        <DialogActions style={{ 
          padding: '24px 40px 32px 40px',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          gap: '12px'
        }}>
          <Button 
            onClick={() => setChallengesDialogOpen(false)}
            style={{
              background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
              color: 'white',
              padding: '12px 32px',
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)'
            }}
          >
            Închide
          </Button>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            style={{
              background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
              color: 'white',
              padding: '12px 32px',
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(212, 176, 138, 0.3)'
            }}
          >
            Descarcă Toate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Playlist Dialog */}
      <Dialog 
        open={playlistDialogOpen} 
        onClose={() => setPlaylistDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Playlist-urile Mele
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#333' }}>
            Colecțiile tale personalizate
          </Typography>
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <MenuBook style={{ fontSize: 48, color: '#D4B08A', marginBottom: '16px' }} />
            <Typography variant="body1" style={{ color: '#666', marginBottom: '8px' }}>
              Nu ai încă playlist-uri create.
            </Typography>
            <Typography variant="body2" style={{ color: '#999' }}>
              Creează colecții personalizate de videouri pentru o experiență organizată.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlaylistDialogOpen(false)}>
            Închide
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog 
        open={historyDialogOpen} 
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Istoricul Complet
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#333' }}>
            Videouri vizionate recent
          </Typography>
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <History style={{ fontSize: 48, color: '#D4B08A', marginBottom: '16px' }} />
            <Typography variant="body1" style={{ color: '#666', marginBottom: '8px' }}>
              Istoricul tău de vizionare este gol.
            </Typography>
            <Typography variant="body2" style={{ color: '#999' }}>
              Videouriile pe care le vei viziona vor apărea aici pentru acces rapid.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>
            Închide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog 
        open={settingsDialogOpen} 
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Setări
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          <Typography variant="h6" style={{ marginBottom: '24px', color: '#333' }}>
            Preferințele tale
          </Typography>
          
          <div style={{ marginBottom: '24px' }}>
            <Typography variant="subtitle1" style={{ marginBottom: '12px', fontWeight: 600 }}>
              Calitatea video
            </Typography>
            <FormControl fullWidth variant="outlined" size="small">
              <Select value="auto" displayEmpty>
                <MenuItem value="auto">Automată</MenuItem>
                <MenuItem value="1080p">1080p HD</MenuItem>
                <MenuItem value="720p">720p HD</MenuItem>
                <MenuItem value="480p">480p</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Typography variant="subtitle1" style={{ marginBottom: '12px', fontWeight: 600 }}>
              Notificări
            </Typography>
            <FormControlLabel
              control={<Switch color="primary" />}
              label="Notificări pentru videouri noi"
            />
            <br />
            <FormControlLabel
              control={<Switch color="primary" />}
              label="Notificări email"
            />
          </div>

          <div>
            <Typography variant="subtitle1" style={{ marginBottom: '12px', fontWeight: 600 }}>
              Limba
            </Typography>
            <FormControl fullWidth variant="outlined" size="small">
              <Select value="ro" displayEmpty>
                <MenuItem value="ro">Română</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>
            Anulează
          </Button>
          <Button 
            onClick={() => {
              setSettingsDialogOpen(false);
              toast.success('Setările au fost salvate!');
            }}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
              color: 'white'
            }}
          >
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Activity Dashboard with Modern Glassmorphism Design */}
      <Dialog 
        open={activityDialogOpen} 
        onClose={() => setActivityDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '24px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, rgba(144, 77, 113, 0.9) 0%, rgba(181, 124, 157, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          color: 'white',
          fontWeight: 600,
          padding: '32px 40px 24px 40px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box style={{ position: 'relative', zIndex: 2 }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Assessment style={{ fontSize: '32px' }} />
              </div>
              <div>
                <Typography variant="h4" style={{ fontWeight: 700, marginBottom: '4px' }}>
                  Activitatea mea
                </Typography>
                <Typography variant="body1" style={{ opacity: 0.9 }}>
                  Dashboard personal de progres și realizări
                </Typography>
              </div>
            </Box>
          </Box>
          
          {/* Admin tracking indicator */}
          {userRank >= 3 && (
            <div style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
              <span style={{ fontSize: '14px' }}>🛡️</span>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                Admin View
              </Typography>
            </div>
          )}
          
          {/* Background decoration */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            zIndex: 1
          }} />
        </DialogTitle>
        
        <DialogContent style={{ 
          padding: '40px',
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          {/* Statistics Overview Cards */}
          <Grid container spacing={4} style={{ marginBottom: '40px' }}>
            {/* Videos Watched Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{
                padding: '28px 24px',
                background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.9) 0%, rgba(41, 128, 185, 0.9) 100%)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 15px 35px rgba(52, 152, 219, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                ':hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 25px 50px rgba(52, 152, 219, 0.4)'
                }
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '64px',
                  height: '64px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '24px'
                }}>
                  📺
                </div>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {activityData.totalVideosWatched || 0}
                </Typography>
                <Typography variant="body1" style={{ opacity: 0.9, fontWeight: 500 }}>
                  Videouri Vizionate
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.7, display: 'block', marginTop: '4px' }}>
                  din {videos.length} disponibile
                </Typography>
              </Card>
            </Grid>

            {/* Watch Time Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{
                padding: '28px 24px',
                background: 'linear-gradient(135deg, rgba(46, 204, 113, 0.9) 0%, rgba(39, 174, 96, 0.9) 100%)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 15px 35px rgba(46, 204, 113, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '64px',
                  height: '64px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '24px'
                }}>
                  ⏰
                </div>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {Math.round((activityData.totalWatchTime || 0) / 60)}h
                </Typography>
                <Typography variant="body1" style={{ opacity: 0.9, fontWeight: 500 }}>
                  Timp de Învățare
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.7, display: 'block', marginTop: '4px' }}>
                  în ultima lună
                </Typography>
              </Card>
            </Grid>

            {/* Completion Rate Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{
                padding: '28px 24px',
                background: 'linear-gradient(135deg, rgba(230, 126, 34, 0.9) 0%, rgba(211, 84, 0, 0.9) 100%)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 15px 35px rgba(230, 126, 34, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '64px',
                  height: '64px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '24px'
                }}>
                  📊
                </div>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {Math.round(activityData.completionRate) || 0}%
                </Typography>
                <Typography variant="body1" style={{ opacity: 0.9, fontWeight: 500 }}>
                  Rata de Finalizare
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.7, display: 'block', marginTop: '4px' }}>
                  media cursurilor
                </Typography>
              </Card>
            </Grid>

            {/* Learning Streak Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{
                padding: '28px 24px',
                background: 'linear-gradient(135deg, rgba(155, 89, 182, 0.9) 0%, rgba(142, 68, 173, 0.9) 100%)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 15px 35px rgba(155, 89, 182, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '64px',
                  height: '64px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '24px'
                }}>
                  🔥
                </div>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {activityData.learningStreak || 0}
                </Typography>
                <Typography variant="body1" style={{ opacity: 0.9, fontWeight: 500 }}>
                  Zile Consecutive
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.7, display: 'block', marginTop: '4px' }}>
                  streak actual
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Detailed Analytics Section */}
          <Grid container spacing={4} style={{ marginBottom: '32px' }}>
            {/* Recent Activity Timeline */}
            <Grid item xs={12} md={6}>
              <Card style={{
                padding: '32px',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '20px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                height: '100%'
              }}>
                <Typography variant="h6" style={{ 
                  color: '#904d71', 
                  marginBottom: '24px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>📈</span>
                  Activitate Recentă
                </Typography>
                
                <div style={{ position: 'relative' }}>
                  {/* Timeline */}
                  <div style={{
                    position: 'absolute',
                    left: '20px',
                    top: '0',
                    bottom: '0',
                    width: '2px',
                    background: 'linear-gradient(to bottom, #904d71, rgba(144, 77, 113, 0.3))',
                    borderRadius: '1px'
                  }} />
                  
                  {/* Activity Items */}
                  {(activityData.recentActivities || [
                    { type: 'video_watched', title: 'Essential Oils Basics', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), duration: 45 },
                    { type: 'challenge_earned', title: 'Advanced Aromatherapy', timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), score: 92 },
                    { type: 'quiz_completed', title: 'Safety Quiz', timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(), score: 88 },
                    { type: 'comment_posted', title: 'Natural Remedies Video', timestamp: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString() }
                  ]).slice(0, 4).map((activity, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      marginBottom: index < 3 ? '24px' : '0',
                      paddingLeft: '56px',
                      position: 'relative'
                    }}>
                      {/* Timeline dot */}
                      <div style={{
                        position: 'absolute',
                        left: '12px',
                        top: '4px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: activity.type === 'challenge_earned' ? '#f39c12' : 
                                   activity.type === 'video_watched' ? '#3498db' :
                                   activity.type === 'quiz_completed' ? '#2ecc71' : '#904d71',
                        border: '3px solid white',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                      }} />
                      
                      <div style={{ flex: 1 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '4px' }}>
                          {activity.type === 'video_watched' ? '📺 Video vizionat' :
                           activity.type === 'challenge_earned' ? '🏆 Provocare obținută' :
                           activity.type === 'quiz_completed' ? '✅ Quiz completat' :
                           activity.type === 'comment_posted' ? '💬 Comentariu postat' : '📚 Activitate'}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                          {activity.title}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#999' }}>
                          {new Date(activity.timestamp).toLocaleDateString('ro-RO', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {activity.score && ` • Scor: ${activity.score}%`}
                          {activity.duration && ` • ${activity.duration} min`}
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Grid>

            {/* Progress Analytics */}
            <Grid item xs={12} md={6}>
              <Card style={{
                padding: '32px',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '20px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                height: '100%'
              }}>
                <Typography variant="h6" style={{ 
                  color: '#904d71', 
                  marginBottom: '24px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>🎯</span>
                  Analiza Progresului
                </Typography>

                {/* Category Progress */}
                <div style={{ marginBottom: '24px' }}>
                  <Typography variant="subtitle2" style={{ marginBottom: '12px', fontWeight: 600 }}>
                    Progres pe Categorii
                  </Typography>
                  {['Începător', 'Intermediar', 'Avansat'].map((category, index) => {
                    const progress = [75, 45, 20][index];
                    const colors = ['#2ecc71', '#f39c12', '#e74c3c'];
                    return (
                      <div key={category} style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {category}
                          </Typography>
                          <Typography variant="caption" style={{ color: colors[index], fontWeight: 600 }}>
                            {progress}%
                          </Typography>
                        </div>
                        <div style={{
                          height: '8px',
                          borderRadius: '4px',
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${progress}%`,
                            height: '100%',
                            background: `linear-gradient(90deg, ${colors[index]}, ${colors[index]}aa)`,
                            borderRadius: '4px',
                            transition: 'width 0.6s ease'
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Achievements */}
                <div>
                  <Typography variant="subtitle2" style={{ marginBottom: '16px', fontWeight: 600 }}>
                    Realizări Recente
                  </Typography>
                  {[
                    { icon: '🎓', title: 'First Challenge', desc: 'Prima provocare obținută' },
                    { icon: '⚡', title: '7-Day Streak', desc: 'O săptămână consecutivă de învățare' },
                    { icon: '📚', title: 'Knowledge Seeker', desc: '10 cursuri finalizate' }
                  ].map((achievement, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      background: 'rgba(144, 77, 113, 0.05)',
                      borderRadius: '12px',
                      marginBottom: '8px',
                      border: '1px solid rgba(144, 77, 113, 0.1)'
                    }}>
                      <span style={{ fontSize: '20px' }}>{achievement.icon}</span>
                      <div>
                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: '2px' }}>
                          {achievement.title}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#666' }}>
                          {achievement.desc}
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Grid>
          </Grid>

          {/* Admin Analytics (only visible to admins) */}
          {userRank >= 3 && (
            <Card style={{
              padding: '32px',
              background: 'linear-gradient(135deg, rgba(144, 77, 113, 0.1) 0%, rgba(181, 124, 157, 0.05) 100%)',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: '2px solid rgba(144, 77, 113, 0.2)',
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(144, 77, 113, 0.15)',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" style={{ 
                color: '#904d71', 
                marginBottom: '24px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '20px' }}>🛡️</span>
                Admin Analytics Dashboard
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <Typography variant="h4" style={{ color: '#904d71', fontWeight: 700, marginBottom: '8px' }}>
                      {filteredCourses.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Content Items
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <Typography variant="h4" style={{ color: '#2ecc71', fontWeight: 700, marginBottom: '8px' }}>
                      {favorites.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      User Favorites
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <Typography variant="h4" style={{ color: '#3498db', fontWeight: 700, marginBottom: '8px' }}>
                      {watchLaterList.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Watch Later Queue
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <Typography variant="h4" style={{ color: '#f39c12', fontWeight: 700, marginBottom: '8px' }}>
                      {Object.keys(videoProgress).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Videos in Progress
                    </Typography>
                  </div>
                </Grid>
              </Grid>
            </Card>
          )}
        </DialogContent>
        
        <DialogActions style={{ 
          padding: '24px 40px 32px 40px',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <Button 
            onClick={() => setActivityDialogOpen(false)}
            style={{
              background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
              color: 'white',
              padding: '12px 32px',
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)',
              transition: 'all 0.3s ease'
            }}
          >
            Închide
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setExportDialogOpen(true);
              setActivityDialogOpen(false);
            }}
            style={{
              background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
              color: 'white',
              padding: '12px 32px',
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(144, 77, 113, 0.3)',
              marginLeft: '12px'
            }}
          >
            <GetApp style={{ marginRight: '8px', fontSize: '18px' }} />
            Export Date
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Export Dialog */}
      <Dialog 
        open={exportDialogOpen} 
        onClose={() => setExportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
          color: 'white',
          fontWeight: 600,
          padding: '24px',
          borderRadius: '16px 16px 0 0'
        }}>
          <GetApp style={{ marginRight: '12px', verticalAlign: 'middle' }} />
          Export Date Utilizatori
        </DialogTitle>
        
        <DialogContent style={{ padding: '32px' }}>
          {/* Data Export Type Section */}
          <Typography variant="h6" style={{ 
            color: '#904d71', 
            marginBottom: '16px',
            fontWeight: 600,
            borderBottom: '2px solid #f0f0f0',
            paddingBottom: '8px'
          }}>
            DATE DE EXPORTAT:
          </Typography>
          
          <Grid container spacing={2} style={{ marginBottom: '32px' }}>
            <Grid item xs={12} sm={6}>
              <Card 
                onClick={() => setExportOptions({...exportOptions, dataFilter: 'filtered'})}
                style={{ 
                  padding: '16px', 
                  cursor: 'pointer',
                  border: exportOptions.dataFilter === 'filtered' ? '2px solid #904d71' : '2px solid #e0e0e0',
                  backgroundColor: exportOptions.dataFilter === 'filtered' ? 'rgba(144, 77, 113, 0.05)' : 'white',
                  borderRadius: '12px'
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#904d71' }}>
                  Date filtrate
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  (2 utilizatori)
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card 
                onClick={() => setExportOptions({...exportOptions, dataFilter: 'all'})}
                style={{ 
                  padding: '16px', 
                  cursor: 'pointer',
                  border: exportOptions.dataFilter === 'all' ? '2px solid #904d71' : '2px solid #e0e0e0',
                  backgroundColor: exportOptions.dataFilter === 'all' ? 'rgba(144, 77, 113, 0.05)' : 'white',
                  borderRadius: '12px'
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#666' }}>
                  Toate datele
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  (2 utilizatori)
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Format Selection Section */}
          <Typography variant="h6" style={{ 
            color: '#904d71', 
            marginBottom: '16px',
            fontWeight: 600,
            borderBottom: '2px solid #f0f0f0',
            paddingBottom: '8px'
          }}>
            FORMAT DE EXPORT:
          </Typography>
          
          <Grid container spacing={2} style={{ marginBottom: '32px' }}>
            {/* Excel Format */}
            <Grid item xs={12} sm={6}>
              <Card 
                onClick={() => setExportOptions({...exportOptions, format: 'excel'})}
                style={{ 
                  padding: '20px', 
                  cursor: 'pointer',
                  border: exportOptions.format === 'excel' ? '2px solid #904d71' : '2px solid #e0e0e0',
                  backgroundColor: exportOptions.format === 'excel' ? 'rgba(144, 77, 113, 0.05)' : 'white',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box style={{
                  backgroundColor: '#1f7a3d',
                  borderRadius: '8px',
                  padding: '12px',
                  marginRight: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <GetApp style={{ color: 'white', fontSize: '24px' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333' }}>
                    Excel (XLSX)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Ideal pentru analiză și editare în Excel
                  </Typography>
                </Box>
              </Card>
            </Grid>

            {/* CSV Format */}
            <Grid item xs={12} sm={6}>
              <Card 
                onClick={() => setExportOptions({...exportOptions, format: 'csv'})}
                style={{ 
                  padding: '20px', 
                  cursor: 'pointer',
                  border: exportOptions.format === 'csv' ? '2px solid #904d71' : '2px solid #e0e0e0',
                  backgroundColor: exportOptions.format === 'csv' ? 'rgba(144, 77, 113, 0.05)' : 'white',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box style={{
                  backgroundColor: '#1976d2',
                  borderRadius: '8px',
                  padding: '12px',
                  marginRight: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <GetApp style={{ color: 'white', fontSize: '24px' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333' }}>
                    CSV
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Format universal, compatibil cu multe aplicații
                  </Typography>
                </Box>
              </Card>
            </Grid>

            {/* PDF Format */}
            <Grid item xs={12} sm={6}>
              <Card 
                onClick={() => setExportOptions({...exportOptions, format: 'pdf'})}
                style={{ 
                  padding: '20px', 
                  cursor: 'pointer',
                  border: exportOptions.format === 'pdf' ? '2px solid #904d71' : '2px solid #e0e0e0',
                  backgroundColor: exportOptions.format === 'pdf' ? 'rgba(144, 77, 113, 0.05)' : 'white',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box style={{
                  backgroundColor: '#d32f2f',
                  borderRadius: '8px',
                  padding: '12px',
                  marginRight: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <GetApp style={{ color: 'white', fontSize: '24px' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333' }}>
                    PDF
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Perfect pentru rapoarte și prezentări
                  </Typography>
                </Box>
              </Card>
            </Grid>

            {/* JSON Format */}
            <Grid item xs={12} sm={6}>
              <Card 
                onClick={() => setExportOptions({...exportOptions, format: 'json'})}
                style={{ 
                  padding: '20px', 
                  cursor: 'pointer',
                  border: exportOptions.format === 'json' ? '2px solid #904d71' : '2px solid #e0e0e0',
                  backgroundColor: exportOptions.format === 'json' ? 'rgba(144, 77, 113, 0.05)' : 'white',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box style={{
                  backgroundColor: '#7b1fa2',
                  borderRadius: '8px',
                  padding: '12px',
                  marginRight: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <GetApp style={{ color: 'white', fontSize: '24px' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333' }}>
                    JSON (Backup)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Backup complet pentru restaurare ulterioară
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* File Name Section */}
          <Box style={{ marginBottom: '24px' }}>
            <Typography variant="body1" style={{ marginBottom: '8px', fontWeight: 500 }}>
              Nume fișier (opțional):
            </Typography>
            <TextField
              fullWidth
              value={exportOptions.fileName}
              onChange={(e) => setExportOptions({...exportOptions, fileName: e.target.value})}
              placeholder="users_export"
              variant="outlined"
              style={{ backgroundColor: '#f8f9fa' }}
              InputProps={{
                style: { borderRadius: '8px' }
              }}
            />
          </Box>

          {/* Data Selection Checkboxes */}
          <Typography variant="h6" style={{ 
            color: '#904d71', 
            marginBottom: '16px',
            fontWeight: 600
          }}>
            Selectați datele de exportat:
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={exportOptions.includeProfile}
                    onChange={(e) => setExportOptions({...exportOptions, includeProfile: e.target.checked})}
                    color="primary"
                  />
                }
                label="Profil utilizator"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={exportOptions.includeVideoProgress}
                    onChange={(e) => setExportOptions({...exportOptions, includeVideoProgress: e.target.checked})}
                    color="primary"
                  />
                }
                label="Progresul videoclipurilor"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={exportOptions.includeFavorites}
                    onChange={(e) => setExportOptions({...exportOptions, includeFavorites: e.target.checked})}
                    color="primary"
                  />
                }
                label="Lista de favorite"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={exportOptions.includeChallenges}
                    onChange={(e) => setExportOptions({...exportOptions, includeChallenges: e.target.checked})}
                    color="primary"
                  />
                }
                label="Provocări obținute"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={exportOptions.includeActivity}
                    onChange={(e) => setExportOptions({...exportOptions, includeActivity: e.target.checked})}
                    color="primary"
                  />
                }
                label="Activitatea utilizatorului"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={exportOptions.includeViewingHistory}
                    onChange={(e) => setExportOptions({...exportOptions, includeViewingHistory: e.target.checked})}
                    color="primary"
                  />
                }
                label="Istoricul de vizionare"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{ padding: '24px', borderTop: '1px solid #f0f0f0' }}>
          <Button 
            onClick={() => setExportDialogOpen(false)}
            style={{ color: '#666', fontWeight: 500 }}
          >
            ANULEAZĂ
          </Button>
          <Button 
            onClick={async () => {
              try {
                // Enhanced export with format selection
                await userProfileService.exportUserData(user?.user?.uid, {
                  format: exportOptions.format,
                  fileName: exportOptions.fileName,
                  includeProfile: exportOptions.includeProfile,
                  includeVideoProgress: exportOptions.includeVideoProgress,
                  includeFavorites: exportOptions.includeFavorites,
                  includeCertificates: exportOptions.includeCertificates,
                  includeActivity: exportOptions.includeActivity,
                  includeSettings: exportOptions.includeSettings,
                  includeViewingHistory: exportOptions.includeViewingHistory,
                  includeWatchLater: exportOptions.includeWatchLater
                });
                
                setExportDialogOpen(false);
                toast.success(`Datele au fost exportate cu succes în format ${exportOptions.format.toUpperCase()}!`);
              } catch (error) {
                console.error('Export error:', error);
                toast.error('Eroare la exportarea datelor: ' + error.message);
              }
            }}
            variant="contained"
            startIcon={<GetApp />}
            style={{
              background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)',
              color: 'white',
              fontWeight: 600,
              padding: '12px 24px',
              borderRadius: '8px',
              textTransform: 'none'
            }}
          >
            EXPORTĂ {exportOptions.format.toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Distribuie
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          <Typography variant="body1" style={{ marginBottom: '16px', color: '#333' }}>
            Distribuie această bibliotecă cu prietenii tăi
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            label="Link de distribuire"
            value="https://theoilsbabe.com/librarie"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Button 
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText('https://theoilsbabe.com/librarie');
                    toast.success('Link copiat în clipboard!');
                  }}
                  style={{ 
                    background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
                    color: 'white',
                    minWidth: 'auto',
                    padding: '4px 12px'
                  }}
                >
                  Copiază
                </Button>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>
            Închide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced YouTube-Style Activity Dialog */}
      <Dialog 
        open={activityDialogOpen} 
        onClose={() => setActivityDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '24px',
            overflow: 'hidden',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #904d71 0%, #7c4a63 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          padding: '24px 32px',
          textAlign: 'center'
        }}>
          📊 Activitatea mea - YouTube Style
        </DialogTitle>
        <DialogContent style={{ padding: '32px', background: '#f8fafc' }}>
          {/* Activity Stats Cards */}
          <Grid container spacing={3} style={{ marginBottom: '32px' }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #904d71 0%, #7c4a63 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(144, 77, 113, 0.3)'
              }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {activityData.totalVideosWatched || 0}
                </Typography>
                <Typography variant="body1">
                  Videoclipuri vizionate
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #a0637a 0%, #8a5670 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(160, 99, 122, 0.3)'
              }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {Math.round((activityData.totalWatchTime || 0) / 60)}h
                </Typography>
                <Typography variant="body1">
                  Timp total vizionare
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #b8749c 0%, #9f6486 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(184, 116, 156, 0.3)'
              }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {activityData.completionRate || 0}%
                </Typography>
                <Typography variant="body1">
                  Rata de finalizare
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #c9859e 0%, #b06f87 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(201, 133, 158, 0.3)'
              }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  {activityData.learningStreak || 0}
                </Typography>
                <Typography variant="body1">
                  Zile consecutive
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Enhanced Engagement Metrics */}
          <Card style={{ 
            padding: '24px', 
            borderRadius: '16px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
              📈 Metrici de Engagement (YouTube Style)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box style={{ textAlign: 'center', padding: '16px' }}>
                  <Typography variant="h4" style={{ fontWeight: 700, color: '#EF4444', marginBottom: '8px' }}>
                    {activityData.totalLikes || 0}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    ❤️ Like-uri date
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box style={{ textAlign: 'center', padding: '16px' }}>
                  <Typography variant="h4" style={{ fontWeight: 700, color: '#904d71', marginBottom: '8px' }}>
                    {activityData.totalComments || 0}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    💬 Comentarii scrise
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box style={{ textAlign: 'center', padding: '16px' }}>
                  <Typography variant="h4" style={{ fontWeight: 700, color: '#a0637a', marginBottom: '8px' }}>
                    {activityData.totalShares || 0}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    🔗 Distribuiri efectuate
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* New Enhanced Features */}
          <Grid container spacing={3} style={{ marginBottom: '24px' }}>
            {/* Daily Activity Chart */}
            <Grid item xs={12} md={6}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                height: '100%'
              }}>
                <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
                  📊 Activitate zilnică (ultimele 7 zile)
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm', 'Dum'].map((day, index) => {
                    const activity = Math.floor(Math.random() * 100) + 20;
                    return (
                      <Box key={day} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Typography style={{ width: '30px', fontSize: '12px', fontWeight: 600 }}>
                          {day}
                        </Typography>
                        <Box style={{ 
                          flex: 1, 
                          background: '#e5e7eb', 
                          borderRadius: '10px', 
                          height: '8px',
                          position: 'relative'
                        }}>
                          <Box style={{
                            background: `linear-gradient(90deg, ${index < 4 ? '#904d71' : '#a0637a'}, ${index < 4 ? '#7c4a63' : '#8a5670'})`,
                            borderRadius: '10px',
                            height: '100%',
                            width: `${activity}%`,
                            transition: 'width 0.3s ease'
                          }} />
                        </Box>
                        <Typography style={{ fontSize: '12px', color: '#666', minWidth: '30px' }}>
                          {activity}%
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Card>
            </Grid>

            {/* Achievement Badges */}
            <Grid item xs={12} md={6}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                height: '100%'
              }}>
                <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
                  🏆 Realizări obținute
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { icon: '🔥', name: 'Primul video', desc: 'Prima vizionare', earned: true },
                    { icon: '⭐', name: 'Explorător', desc: '10 videouri', earned: true },
                    { icon: '🎯', name: 'Expert', desc: '50 videouri', earned: true },
                    { icon: '💎', name: 'Maestru', desc: '100 videouri', earned: false },
                    { icon: '🚀', name: 'Pionier', desc: '5 provocări', earned: false },
                    { icon: '👑', name: 'Campion', desc: '10 provocări', earned: false }
                  ].map((badge, index) => (
                    <Grid item xs={6} key={index}>
                      <Box style={{
                        textAlign: 'center',
                        padding: '12px',
                        background: badge.earned ? 'linear-gradient(135deg, #904d71, #7c4a63)' : '#f3f4f6',
                        borderRadius: '12px',
                        color: badge.earned ? 'white' : '#9ca3af',
                        opacity: badge.earned ? 1 : 0.6,
                        transition: 'all 0.3s ease'
                      }}>
                        <Typography style={{ fontSize: '20px', marginBottom: '4px' }}>
                          {badge.icon}
                        </Typography>
                        <Typography style={{ fontSize: '11px', fontWeight: 600, marginBottom: '2px' }}>
                          {badge.name}
                        </Typography>
                        <Typography style={{ fontSize: '9px' }}>
                          {badge.desc}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          </Grid>

          {/* Weekly Goals */}
          <Card style={{ 
            padding: '24px', 
            borderRadius: '16px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
              🎯 Obiectivele săptămânii
            </Typography>
            <Grid container spacing={3}>
              {[
                { goal: 'Vizionează 5 videouri noi', progress: 60, current: 3, target: 5, icon: '📺' },
                { goal: 'Câștigă 2 provocări', progress: 50, current: 1, target: 2, icon: '🏆' },
                { goal: 'Petrece 2 ore învățând', progress: 75, current: 90, target: 120, icon: '⏰', unit: 'min' }
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box style={{ 
                    background: 'linear-gradient(135deg, rgba(144, 77, 113, 0.1), rgba(124, 74, 99, 0.1))',
                    borderRadius: '12px',
                    padding: '16px'
                  }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Typography style={{ fontSize: '18px' }}>{item.icon}</Typography>
                      <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                        {item.goal}
                      </Typography>
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Typography style={{ fontSize: '12px', color: '#6b7280' }}>
                        {item.current} / {item.target} {item.unit || ''}
                      </Typography>
                      <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#904d71' }}>
                        {item.progress}%
                      </Typography>
                    </Box>
                    <Box style={{ 
                      background: '#e5e7eb', 
                      borderRadius: '10px', 
                      height: '6px',
                      position: 'relative'
                    }}>
                      <Box style={{
                        background: 'linear-gradient(90deg, #904d71, #7c4a63)',
                        borderRadius: '10px',
                        height: '100%',
                        width: `${item.progress}%`,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>

          {/* Learning Progress */}
          <Card style={{ 
            padding: '24px', 
            borderRadius: '16px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
              🎓 Progres de Învățare
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box style={{ marginBottom: '16px' }}>
                  <Typography variant="body1" style={{ fontWeight: 600, marginBottom: '8px' }}>
                    Cursuri completate: {activityData.completedCourses || 0}/10
                  </Typography>
                  <Box style={{ 
                    background: '#e5e7eb', 
                    borderRadius: '10px', 
                    height: '10px',
                    position: 'relative'
                  }}>
                    <Box style={{
                      background: 'linear-gradient(90deg, #904d71, #7c4a63)',
                      borderRadius: '10px',
                      height: '100%',
                      width: `${((activityData.completedCourses || 0) / 10) * 100}%`,
                      transition: 'width 0.3s ease'
                    }} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box style={{ marginBottom: '16px' }}>
                  <Typography variant="body1" style={{ fontWeight: 600, marginBottom: '8px' }}>
                    Provocări obținute: {activityData.certificates || 0}
                  </Typography>
                  <Box style={{ 
                    background: '#e5e7eb', 
                    borderRadius: '10px', 
                    height: '10px',
                    position: 'relative'
                  }}>
                    <Box style={{
                      background: 'linear-gradient(90deg, #a0637a, #8a5670)',
                      borderRadius: '10px',
                      height: '100%',
                      width: `${Math.min(((activityData.certificates || 0) / 5) * 100, 100)}%`,
                      transition: 'width 0.3s ease'
                    }} />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* Recent Activity Timeline */}
          <Card style={{ 
            padding: '24px', 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
              ⏰ Activitate recentă
            </Typography>
            <List>
              {activityData.recentActivities && activityData.recentActivities.length > 0 ? (
                activityData.recentActivities.slice(0, 3).map((activity, index) => (
                  <ListItem key={index} style={{ padding: '12px 0' }}>
                    <Avatar style={{ 
                      background: index === 0 ? '#904d71' : index === 1 ? '#a0637a' : '#b8749c', 
                      marginRight: '16px' 
                    }}>
                      {activity.type === 'video_watched' && <VideoLibrary />}
                      {activity.type === 'challenge_earned' && <School />}
                      {activity.type === 'favorite_added' && <Favorite />}
                      {!['video_watched', 'challenge_earned', 'favorite_added'].includes(activity.type) && <VideoLibrary />}
                    </Avatar>
                    <ListItemText 
                      primary={
                        activity.type === 'video_watched' 
                          ? `Ai vizionat '${activity.title}'`
                          : activity.type === 'challenge_earned' 
                          ? `Provocare gata pentru '${activity.title}'`
                          : activity.type === 'favorite_added'
                          ? `Ai dat like la ${activity.title}`
                          : activity.title
                      }
                      secondary={
                        activity.type === 'video_watched' 
                          ? `${activity.duration ? `${activity.duration} min • ` : ''}${new Date(activity.timestamp).toLocaleDateString('ro-RO')}`
                          : activity.type === 'challenge_earned' 
                          ? `${activity.score ? `Rezultat: ${activity.score}% • ` : ''}${new Date(activity.timestamp).toLocaleDateString('ro-RO')}`
                          : new Date(activity.timestamp).toLocaleDateString('ro-RO')
                      }
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                ))
              ) : (
                <>
                  <ListItem style={{ padding: '12px 0' }}>
                    <Avatar style={{ background: '#904d71', marginRight: '16px' }}>
                      <VideoLibrary />
                    </Avatar>
                    <ListItemText 
                      primary="Ai vizionat 'Essential Oils Basics'"
                      secondary="Acum 2 ore • 45 min"
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                  <ListItem style={{ padding: '12px 0' }}>
                    <Avatar style={{ background: '#a0637a', marginRight: '16px' }}>
                      <School />
                    </Avatar>
                    <ListItemText 
                      primary="Provocare gata pentru 'Advanced Aromatherapy'"
                      secondary="Ieri • Rezultat: 92%"
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                  <ListItem style={{ padding: '12px 0' }}>
                    <Avatar style={{ background: '#b8749c', marginRight: '16px' }}>
                      <Favorite />
                    </Avatar>
                    <ListItemText 
                      primary="Ai dat like la 3 videoclipuri noi"
                      secondary="Acum 3 zile"
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                </>
              )}
            </List>
          </Card>
        </DialogContent>
        <DialogActions style={{ padding: '24px 32px' }}>
          <Button 
            onClick={() => setActivityDialogOpen(false)}
            style={{
              background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '12px',
              fontWeight: 600
            }}
          >
            Închide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Export Dialog - Admin Page Style */}
      <Dialog 
        open={exportDialogOpen} 
        onClose={() => setExportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '24px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #904d71 0%, #7c4a63 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          padding: '24px 32px',
          textAlign: 'center'
        }}>
          📥 Export Date Utilizatori - Admin Style
        </DialogTitle>
        <DialogContent style={{ padding: '32px', background: '#f8fafc' }}>
          {/* Export Format Selection Cards */}
          <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
            Selectează formatul de export:
          </Typography>
          <Grid container spacing={3} style={{ marginBottom: '32px' }}>
            {[
              { format: 'excel', icon: '📊', name: 'Excel (.xlsx)', desc: 'Foaie de calcul profesională', color: '#904d71' },
              { format: 'csv', icon: '📋', name: 'CSV (.csv)', desc: 'Format universal de date', color: '#a0637a' },
              { format: 'pdf', icon: '📄', name: 'PDF (.pdf)', desc: 'Document formatat', color: '#7c4a63' },
              { format: 'json', icon: '💾', name: 'JSON (.json)', desc: 'Format pentru dezvoltatori', color: '#8a5670' }
            ].map((formatOption) => (
              <Grid item xs={12} sm={6} key={formatOption.format}>
                <Card 
                  onClick={() => setExportOptions({...exportOptions, format: formatOption.format})}
                  style={{ 
                    padding: '20px', 
                    borderRadius: '16px',
                    cursor: 'pointer',
                    border: exportOptions.format === formatOption.format ? `3px solid ${formatOption.color}` : '2px solid #e5e7eb',
                    background: exportOptions.format === formatOption.format ? `${formatOption.color}10` : 'white',
                    transition: 'all 0.3s ease',
                    boxShadow: exportOptions.format === formatOption.format ? `0 8px 32px ${formatOption.color}30` : '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <Box style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Typography variant="h3">{formatOption.icon}</Typography>
                    <Box>
                      <Typography variant="h6" style={{ fontWeight: 700, color: '#1f2937' }}>
                        {formatOption.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatOption.desc}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Export Options */}
          <Card style={{ 
            padding: '24px', 
            borderRadius: '16px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
              ⚙️ Opțiuni de export:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportOptions.includeActivity}
                      onChange={(e) => setExportOptions({...exportOptions, includeActivity: e.target.checked})}
                      style={{ color: '#10B981' }}
                    />
                  }
                  label="Include activitatea utilizatorului"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportOptions.includeCertificates}
                      onChange={(e) => setExportOptions({...exportOptions, includeCertificates: e.target.checked})}
                      style={{ color: '#10B981' }}
                    />
                  }
                  label="Include certificatele"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportOptions.includeProgress}
                      onChange={(e) => setExportOptions({...exportOptions, includeProgress: e.target.checked})}
                      style={{ color: '#10B981' }}
                    />
                  }
                  label="Include progresul cursurilor"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportOptions.includePreferences}
                      onChange={(e) => setExportOptions({...exportOptions, includePreferences: e.target.checked})}
                      style={{ color: '#10B981' }}
                    />
                  }
                  label="Include preferințele"
                />
              </Grid>
            </Grid>
          </Card>

          {/* Date Range Selection */}
          <Card style={{ 
            padding: '24px', 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
              📅 Perioada de export:
            </Typography>
            
            {/* Quick Time Period Selection */}
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '16px', color: '#4B5563' }}>
              Selectează perioada rapidă:
            </Typography>
            
            <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: '24px', maxWidth: '300px' }}>
              <Select
                value={exportOptions.quickPeriod || ''}
                onChange={(e) => {
                  const period = e.target.value;
                  if (period) {
                    const now = new Date();
                    let startDate = new Date();
                    
                    switch(period) {
                      case '1d':
                        startDate.setDate(now.getDate() - 1);
                        break;
                      case '1w':
                        startDate.setDate(now.getDate() - 7);
                        break;
                      case '1m':
                        startDate.setMonth(now.getMonth() - 1);
                        break;
                      case '3m':
                        startDate.setMonth(now.getMonth() - 3);
                        break;
                      case '1y':
                        startDate.setFullYear(now.getFullYear() - 1);
                        break;
                      default:
                        startDate = new Date();
                    }
                    
                    setExportOptions({
                      ...exportOptions,
                      quickPeriod: period,
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: now.toISOString().split('T')[0]
                    });
                  } else {
                    setExportOptions({
                      ...exportOptions,
                      quickPeriod: null,
                      startDate: '',
                      endDate: ''
                    });
                  }
                }}
                displayEmpty
                style={{
                  borderRadius: '12px',
                  background: 'white',
                  fontSize: '0.9rem'
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#904d71',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#904d71',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  }
                }}
              >
                <MenuItem value="" style={{ fontSize: '0.9rem', color: '#6B7280', padding: '8px 16px' }}>
                  <em>Selectează o perioadă rapidă</em>
                </MenuItem>
                <MenuItem value="1d" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                  📅 Ultima zi
                </MenuItem>
                <MenuItem value="1w" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                  🗓️ Ultima săptămână
                </MenuItem>
                <MenuItem value="1m" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                  📆 Ultima lună
                </MenuItem>
                <MenuItem value="3m" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                  🗂️ Ultimele 3 luni
                </MenuItem>
                <MenuItem value="1y" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                  📋 Ultimul an
                </MenuItem>
              </Select>
            </FormControl>
            
            <Divider style={{ margin: '16px 0' }} />
            
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '16px', color: '#4B5563' }}>
              Sau selectează perioada personalizată:
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de început"
                  value={exportOptions.startDate}
                  onChange={(e) => setExportOptions({...exportOptions, startDate: e.target.value, quickPeriod: null})}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#904d71',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#904d71',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#904d71',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de sfârșit"
                  value={exportOptions.endDate}
                  onChange={(e) => setExportOptions({...exportOptions, endDate: e.target.value, quickPeriod: null})}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#904d71',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#904d71',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#904d71',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Card>
        </DialogContent>
        <DialogActions style={{ padding: '24px 32px', gap: '12px' }}>
          <Button 
            onClick={() => setExportDialogOpen(false)}
            style={{
              background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '12px',
              fontWeight: 600
            }}
          >
            Anulează
          </Button>
          <Button 
            onClick={async () => {
              try {
                const userIdToUse = user?.user?.uid || user?.user?.id || user?.uid || user?.id;
                console.log('User object:', user);
                console.log('Using user ID:', userIdToUse);
                console.log('Starting export with options:', exportOptions);
                
                if (!userIdToUse) {
                  toast.error('ID utilizator necunoscut. Te rog să te conectezi din nou.');
                  return;
                }
                
                const result = await userProfileService.exportUserData(userIdToUse, exportOptions);
                setExportDialogOpen(false);
                
                if (result.isLocalFallback) {
                  toast.warning(`Export local generat: ${result.filename}. Serverul nu este disponibil momentan.`);
                } else {
                  toast.success(`Date exportate cu succes în format ${exportOptions.format?.toUpperCase()}! Fișier: ${result.filename}`);
                }
              } catch (error) {
                console.error('Export failed:', error);
                toast.error(`Eroare la exportul datelor: ${error.message || 'Eroare necunoscută'}. Te rog să încerci din nou.`);
              }
            }}
            disabled={!exportOptions.format}
            style={{
              background: exportOptions.format ? 'linear-gradient(135deg, #904d71 0%, #7c4a63 100%)' : '#9CA3AF',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '12px',
              fontWeight: 600
            }}
          >
            🚀 Exportă datele
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modern Challenge Management Dialog */}
      <Dialog 
        open={challengesDialogOpen} 
        onClose={() => setChallengesDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '24px',
            overflow: 'hidden',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          padding: '24px 32px',
          textAlign: 'center'
        }}>
          🏆 Management Certificate - High Class
        </DialogTitle>
        <DialogContent style={{ padding: '32px', background: '#f8fafc' }}>
          {/* Certificate Statistics */}
          <Grid container spacing={3} style={{ marginBottom: '32px' }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(245, 158, 11, 0.3)'
              }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  5
                </Typography>
                <Typography variant="body1">
                  Certificate obținute
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)'
              }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  92%
                </Typography>
                <Typography variant="body1">
                  Scor mediu
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)'
              }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  3
                </Typography>
                <Typography variant="body1">
                  Certificate premium
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ 
                padding: '24px', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)'
              }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                  2
                </Typography>
                <Typography variant="body1">
                  În progres
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Modern Certificate Gallery */}
          <Typography variant="h6" style={{ fontWeight: 700, marginBottom: '20px', color: '#1f2937' }}>
            🎨 Galeria mea de certificate
          </Typography>
          <Grid container spacing={3}>
            {[
              { title: 'Essential Oils Basics', date: '15 Jan 2024', score: 95, status: 'completed', level: 'Premium' },
              { title: 'Advanced Aromatherapy', date: '20 Dec 2023', score: 88, status: 'completed', level: 'Standard' },
              { title: 'Business Essentials', date: '10 Nov 2023', score: 92, status: 'completed', level: 'Premium' },
              { title: 'Wellness Coach Certification', date: 'În progres', score: 75, status: 'in-progress', level: 'Premium' },
              { title: 'Natural Skincare Expert', date: 'În progres', score: 60, status: 'in-progress', level: 'Standard' }
            ].map((cert, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card style={{ 
                  borderRadius: '24px',
                  overflow: 'hidden',
                  background: cert.status === 'completed' 
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'rgba(255, 248, 233, 0.95)',
                  backdropFilter: 'blur(25px)',
                  WebkitBackdropFilter: 'blur(25px)',
                  border: cert.level === 'Premium' 
                    ? '1px solid rgba(245, 158, 11, 0.3)' 
                    : '1px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: cert.level === 'Premium'
                    ? '0 20px 60px rgba(245, 158, 11, 0.25), 0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
                    : '0 20px 60px rgba(144, 77, 113, 0.15), 0 8px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255, 255, 255, 0.7)',
                  position: 'relative',
                  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: cert.level === 'Premium'
                      ? '0 30px 80px rgba(245, 158, 11, 0.35), 0 12px 48px rgba(0,0,0,0.12)'
                      : '0 30px 80px rgba(144, 77, 113, 0.25), 0 12px 48px rgba(0,0,0,0.10)'
                  }
                }}>
                  {/* Glass reflection overlay */}
                  <Box style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%)',
                    pointerEvents: 'none',
                    zIndex: 1
                  }} />
                  
                  {/* Premium Badge with enhanced glass effect */}
                  {cert.level === 'Premium' && (
                    <Box style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'rgba(245, 158, 11, 0.9)',
                      backdropFilter: 'blur(15px)',
                      WebkitBackdropFilter: 'blur(15px)',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '24px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      zIndex: 2,
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 8px 24px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
                    }}>
                      ⭐ PREMIUM
                    </Box>
                  )}
                  
                  <CardContent style={{ 
                    padding: '32px',
                    background: 'transparent',
                    position: 'relative',
                    zIndex: 2
                  }}>
                    <Box style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
                      <Avatar style={{
                        background: cert.status === 'completed' 
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)'
                          : 'linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 100%)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        width: '72px',
                        height: '72px',
                        marginRight: '20px',
                        border: '2px solid rgba(255, 255, 255, 0.4)',
                        boxShadow: '0 12px 32px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                        fontSize: '28px'
                      }}>
                        {cert.status === 'completed' ? '🏆' : '⏳'}
                      </Avatar>
                      <Box style={{ flex: 1 }}>
                        <Typography variant="h6" style={{ 
                          fontWeight: 700, 
                          marginBottom: '8px',
                          color: '#1f2937',
                          fontSize: '1.25rem'
                        }}>
                          {cert.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ 
                          marginBottom: '12px',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}>
                          {cert.date}
                        </Typography>
                        <Chip 
                          label={cert.status === 'completed' ? 'Finalizat' : 'În progres'}
                          style={{
                            background: cert.status === 'completed' 
                              ? 'rgba(16, 185, 129, 0.9)'
                              : 'rgba(245, 158, 11, 0.9)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Progress Bar with glass effect */}
                    <Box style={{ marginBottom: '20px' }}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <Typography variant="body2" style={{ 
                          fontWeight: 600,
                          color: '#374151',
                          fontSize: '0.9rem'
                        }}>
                          Progres
                        </Typography>
                        <Typography variant="body2" style={{ 
                          fontWeight: 700, 
                          color: cert.status === 'completed' ? '#10B981' : '#F59E0B',
                          fontSize: '0.9rem'
                        }}>
                          {cert.score}%
                        </Typography>
                      </Box>
                      <Box style={{ 
                        background: 'rgba(229, 231, 235, 0.8)', 
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        borderRadius: '12px', 
                        height: '10px',
                        position: 'relative',
                        border: '1px solid rgba(255, 255, 255, 0.5)',
                        overflow: 'hidden'
                      }}>
                        <Box style={{
                          background: cert.status === 'completed' 
                            ? 'linear-gradient(90deg, rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.9))'
                            : 'linear-gradient(90deg, rgba(245, 158, 11, 0.9), rgba(217, 119, 6, 0.9))',
                          backdropFilter: 'blur(5px)',
                          WebkitBackdropFilter: 'blur(5px)',
                          borderRadius: '12px',
                          height: '100%',
                          width: `${cert.score}%`,
                          transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                        }} />
                      </Box>
                    </Box>

                    {/* Action Buttons with enhanced glass effects */}
                    <Box style={{ display: 'flex', gap: '12px' }}>
                      {cert.status === 'completed' ? (
                        <>
                          <Button 
                            size="small"
                            style={{
                              background: 'rgba(16, 185, 129, 0.9)',
                              backdropFilter: 'blur(15px)',
                              WebkitBackdropFilter: 'blur(15px)',
                              color: 'white',
                              fontWeight: 600,
                              borderRadius: '12px',
                              flex: 1,
                              padding: '12px 16px',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                              transition: 'all 0.3s ease',
                              textTransform: 'none',
                              '&:hover': {
                                background: 'rgba(16, 185, 129, 1)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4)'
                              }
                            }}
                          >
                            📥 Descarcă
                          </Button>
                          <Button 
                            size="small"
                            style={{
                              background: 'rgba(59, 130, 246, 0.9)',
                              backdropFilter: 'blur(15px)',
                              WebkitBackdropFilter: 'blur(15px)',
                              color: 'white',
                              fontWeight: 600,
                              borderRadius: '12px',
                              flex: 1,
                              padding: '12px 16px',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                              transition: 'all 0.3s ease',
                              textTransform: 'none',
                              '&:hover': {
                                background: 'rgba(59, 130, 246, 1)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 32px rgba(59, 130, 246, 0.4)'
                              }
                            }}
                          >
                            👁️ Vezi
                          </Button>
                        </>
                      ) : (
                        <Button 
                          size="small"
                          style={{
                            background: 'rgba(245, 158, 11, 0.9)',
                            backdropFilter: 'blur(15px)',
                            WebkitBackdropFilter: 'blur(15px)',
                            color: 'white',
                            fontWeight: 600,
                            borderRadius: '12px',
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                            transition: 'all 0.3s ease',
                            textTransform: 'none',
                            '&:hover': {
                              background: 'rgba(245, 158, 11, 1)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 12px 32px rgba(245, 158, 11, 0.4)'
                            }
                          }}
                        >
                          📚 Continuă cursul
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: '24px 32px' }}>
          <Button 
            onClick={() => setChallengesDialogOpen(false)}
            style={{
              background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '12px',
              fontWeight: 600
            }}
          >
            Închide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downloads Dialog */}
      <Dialog
        open={downloadsDialogOpen}
        onClose={() => setDownloadsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{
          color: '#503247',
          fontFamily: '"Times New Roman MT", "Times New Roman", serif',
          fontSize: '1.75rem',
          borderBottom: '1px solid rgba(144, 77, 113, 0.16)'
        }}>
          Descărcările mele
        </DialogTitle>
        <DialogContent style={{ padding: '20px 24px' }}>
          {downloadQueue.length === 0 ? (
            <Box style={{ padding: '28px 8px', textAlign: 'center' }}>
              <GetApp style={{ color: '#904d71', fontSize: 42, marginBottom: 10 }} />
              <Typography style={{ color: '#6d5b67' }}>
                Nu ai încă descărcări salvate.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {downloadQueue.map((download, index) => {
                const downloadUrl = download.fileUrl || download.downloadUrl;
                const dateValue = download.downloadedAt || download.createdAt;
                const date = dateValue ? new Date(dateValue) : null;

                return (
                  <React.Fragment key={download.id || `${download.itemId || 'download'}-${index}`}>
                    <ListItem style={{ padding: '14px 0' }}>
                      <ListItemIcon>
                        <GetApp style={{ color: '#904d71' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={download.title || 'Fișier descărcat'}
                        secondary={[
                          download.itemType,
                          download.format,
                          date && !Number.isNaN(date.getTime())
                            ? date.toLocaleDateString('ro-RO')
                            : null
                        ].filter(Boolean).join(' • ')}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!downloadUrl}
                        onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}
                        style={{
                          color: '#904d71',
                          borderColor: 'rgba(144, 77, 113, 0.42)',
                          borderRadius: 999,
                          textTransform: 'none'
                        }}
                      >
                        Deschide
                      </Button>
                    </ListItem>
                    {index < downloadQueue.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '14px 24px 20px' }}>
          {downloadQueue.length > 0 && (
            <Button
              onClick={async () => {
                const userId = user?.user?.uid || user?.user?.id || user?.uid || user?.id;
                if (!userId) return;

                try {
                  await downloadsService.clearDownloadHistory(userId);
                  setDownloadQueue([]);
                  toast.success('Istoricul descărcărilor a fost golit');
                } catch (error) {
                  console.error('Error clearing download history:', error);
                  toast.error('Nu am putut goli istoricul descărcărilor');
                }
              }}
              style={{ color: '#8b3f55', textTransform: 'none' }}
            >
              Golește istoricul
            </Button>
          )}
          <Button
            onClick={() => setDownloadsDialogOpen(false)}
            variant="contained"
            style={{
              background: '#904d71',
              color: '#fff',
              borderRadius: 999,
              textTransform: 'none',
              boxShadow: 'none'
            }}
          >
            Închide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Raportează o problemă
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          <Typography variant="body1" style={{ marginBottom: '16px', color: '#333' }}>
            Descrie problema întâmpinată
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Descrierea problemei"
            placeholder="Te rog să descrii detaliat problema..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>
            Anulează
          </Button>
          <Button 
            onClick={() => {
              setReportDialogOpen(false);
              toast.success('Raportul a fost trimis! Mulțumim!');
            }}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #D4B08A 0%, #C19A6B 100%)',
              color: 'white'
            }}
          >
            Trimite raport
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Admin Analytics Dialog */}
      <Dialog 
        open={adminDashboardOpen} 
        onClose={() => setAdminDashboardOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">📊 Quick Analytics Dashboard</Typography>
          <Box style={{ display: 'flex', gap: '8px' }}>
            <Button
              onClick={() => setAdminDashboardOpen(false)}
              style={{ color: 'white', minWidth: 'auto', padding: '4px' }}
            >
              ✕
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: '0px' }}>
          <Suspense fallback={
            <Box style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '400px' 
            }}>
              <CircularProgress size={60} style={{ color: '#904d71' }} />
              <Typography variant="h6" style={{ marginLeft: '16px' }}>
                Loading Admin Dashboard...
              </Typography>
            </Box>
          }>
            <AdminDashboard />
          </Suspense>
        </DialogContent>
      </Dialog>

      {/* Video Upload Dialog - Admin Only */}
      <Dialog 
        open={videoUploadDialogOpen} 
        onClose={() => setVideoUploadDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #904d71 0%, #b57c9d 100%)', 
          color: 'white',
          textAlign: 'center'
        }}>
          Upload New Video
        </DialogTitle>
        <DialogContent style={{ padding: '20px' }}>
          <Suspense fallback={<div>Loading...</div>}>
            <VideoUploadForm onClose={() => setVideoUploadDialogOpen(false)} />
          </Suspense>
        </DialogContent>
      </Dialog>
      <NewFooter/>
      <NewMadeWith/>
      <NewScrollTop/>
    </>
  );
});

const mapStateToProps = (state) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(LibraryPage);
